import React, { useState, useEffect, useRef, useCallback, useMemo, createContext, useContext } from 'react';
import {
  Play, Pause, SkipBack, SkipForward, Volume2, VolumeX,
  Maximize2, Minimize2, Activity, Brain, Users, MessageSquare,
  Send, Loader2, Target, Shield, Heart, Zap, Video, Check, Database, Search, Clock,
  ChevronDown, ChevronLeft, ChevronRight, Eye, Layers, Grid3X3, RefreshCw, Mic, Crosshair, X, HelpCircle
} from 'lucide-react';
import { marked } from 'marked';
import DOMPurify from 'dompurify';
import { Panel, Group as PanelGroup, Separator as PanelResizeHandle } from 'react-resizable-panels';
import MCPAppRenderer from '../../components/chat/MCPAppRenderer';

import { useTeam } from '../../lib/teamConfig';
import usePersistedState from '../../hooks/usePersistedState';

// Configure marked for safe rendering
marked.setOptions({
  breaks: true,
  gfm: true,
});

// Simple markdown renderer component for chat messages
const MarkdownMessage = ({ content, onNavigateSegment }) => {
  const containerRef = useRef(null);
  const html = useMemo(() => {
    // Strip heatmap data markers before rendering
    let cleaned = (content || '').replace(/<!--HEATMAP_DATA:.*?-->/s, '');
    // Convert [Segment N (...)] to clickable segment links before markdown parsing
    cleaned = cleaned.replace(
      /\[Segment (\d+)(?:\s*\(([^)]*)\))?\]/g,
      (match, segNum, timeRange) => {
        const label = timeRange ? `Segment ${segNum} (${timeRange})` : `Segment ${segNum}`;
        return `<button data-segment="${segNum}" class="segment-link">${label}</button>`;
      }
    );
    return DOMPurify.sanitize(marked.parse(cleaned));
  }, [content]);

  // Attach click handlers to segment links
  useEffect(() => {
    if (!containerRef.current || !onNavigateSegment) return;
    const links = containerRef.current.querySelectorAll('.segment-link');
    const handler = (e) => {
      // AI uses 1-based segment numbers ("Segment 47" = 47th segment)
      // But our state is 0-based (currentSegment = 0 is first segment)
      // So we need to subtract 1 when user clicks a segment link
      const seg = parseInt(e.target.dataset.segment) - 1; // Convert 1-based to 0-based
      if (!isNaN(seg) && seg >= 0) onNavigateSegment(seg);
    };
    links.forEach(link => link.addEventListener('click', handler));
    return () => links.forEach(link => link.removeEventListener('click', handler));
  }, [html, onNavigateSegment]);

  return (
    <div
      ref={containerRef}
      className="prose prose-invert prose-sm max-w-none
        prose-p:my-1.5 prose-p:text-white/90 prose-p:text-sm prose-p:leading-relaxed
        prose-strong:text-amber-400 prose-strong:font-semibold
        prose-em:text-cyan-300 prose-em:not-italic
        prose-ul:my-1.5 prose-ul:pl-4 prose-li:text-white/80 prose-li:text-sm prose-li:my-0.5
        prose-ol:my-1.5 prose-ol:pl-4
        prose-headings:text-amber-400 prose-headings:font-medium prose-headings:mt-2 prose-headings:mb-1
        prose-h1:text-base prose-h2:text-sm prose-h3:text-sm
        prose-code:text-cyan-300 prose-code:bg-zinc-800/50 prose-code:px-1 prose-code:rounded prose-code:text-xs
        prose-pre:bg-zinc-800/50 prose-pre:p-2 prose-pre:rounded-lg prose-pre:my-1
        prose-blockquote:border-l-2 prose-blockquote:border-amber-500/50 prose-blockquote:pl-3 prose-blockquote:my-1 prose-blockquote:text-white/70
        [&_.segment-link]:text-cyan-400 [&_.segment-link]:underline [&_.segment-link]:cursor-pointer [&_.segment-link]:bg-transparent [&_.segment-link]:border-none [&_.segment-link]:p-0 [&_.segment-link]:font-medium [&_.segment-link]:text-sm hover:[&_.segment-link]:text-cyan-300"
      dangerouslySetInnerHTML={{ __html: html }}
    />
  );
};

// Memoized component to extract and render heatmap data from chat messages (#8 fix)
const HeatmapFromContent = React.memo(({ content }) => {
  const data = useMemo(() => {
    const match = content?.match(/<!--HEATMAP_DATA:(.*?)-->/s);
    if (!match) return null;
    try { return JSON.parse(match[1]); } catch { return null; }
  }, [content]);
  if (!data) return null;
  return <MCPAppRenderer resourceUri={data.resourceUri} toolName={data.toolName} toolResult={data} />;
});

// ============================================================================
// CONFIGURATION
// ============================================================================

const GAMES = [
  { id: 'SPURS_AT_OKC_01.13.26', name: 'Spurs @ OKC', date: 'Jan 13, 2026', folder: 'SPURS AT OKC 01.13.26', segments: 128, duration: 7680 },
  { id: 'SPURS_VS_OKC_12.13.25', name: 'Spurs vs OKC', date: 'Dec 13, 2025', folder: 'SPURS VS OKC 12.13.25', segments: 140, duration: 8400 },
  { id: 'SPURS_VS_OKC_12.25.25', name: 'Spurs vs OKC', date: 'Dec 25, 2025', folder: 'SPURS VS OKC 12.25.25', segments: 123, duration: 7380 },
  { id: 'SPURS_vs._OKC_12.23.25', name: 'Spurs vs OKC', date: 'Dec 23, 2025', folder: 'SPURS vs. OKC 12.23.25', segments: 116, duration: 6960 },
];

const GAME_IDS = GAMES.map(g => g.id);
const GAME_SET_ID = 'wch_spurs-vs-okc_2026-01-24_2020';
const GCS_BUCKET = 'slam-video-analysis';
const SEGMENT_DURATION = 60; // seconds per segment

const TOUR_STEPS = [
  {
    target: 'game-selector',
    title: 'Start With Game Context',
    description: 'Pick which Spurs vs OKC game is active. Every widget updates to this game, so always confirm this first.',
    lookFor: 'Look for date + segment count changes when you switch games.',
  },
  {
    target: 'video-widget',
    title: 'Video = Ground Truth',
    description: 'This is the source clip for the current segment. Use it to validate every tactical, biomech, and chat conclusion.',
    lookFor: 'Look for possession type, spacing shape, and first defensive reaction.',
  },
  {
    target: 'timeline-widget',
    title: 'Timeline Risk Scanner',
    description: 'Each bar is a segment. Color hints show where risk or analysis density is concentrated.',
    lookFor: 'Jump to yellow/red zones first for potential leverage or concern moments.',
  },
  {
    target: 'analysis-widget',
    title: 'Segment Analysis Tabs',
    description: 'Use Tactical, Biomech, Shooting, Cultural, Audio, and Cognitive tabs to isolate one lens at a time before combining conclusions.',
    lookFor: 'In Biomech, prioritize repeated high-risk signals; in Tactical, compare what worked vs failed.',
  },
  {
    target: 'viz-widget',
    title: 'Visualizations + Snapshot',
    description: 'This panel compresses key metrics and drive patterns so you can pattern-match faster than raw text alone.',
    lookFor: 'Align drive-type outcomes with risk/energy to spot repeatable decision edges.',
  },
  {
    target: 'chat-widget',
    title: 'Game Intelligence Chat',
    description: 'Ask targeted questions to synthesize evidence across video, segment analysis, and game-level context.',
    lookFor: 'Ask comparative prompts like “what changed after segment X” for higher-signal answers.',
  },
  {
    target: 'context-levels',
    title: 'Scope Control: Segment / Game / League',
    description: 'This changes the evidence scope for AI. Segment is precise, Game is aggregate, League pulls broader NBA context.',
    lookFor: 'Use Segment for clip truth, Game for trend confirmation, League for external benchmark context.',
  },
];

// ============================================================================
// STATE PERSISTENCE HELPER
// ============================================================================

// ============================================================================
// UTILITIES
// ============================================================================

// Use backend proxy to avoid CORS issues when fetching from GCS
const gcsToProxyUrl = (path) => `/api/gcs-proxy/${path}?bucket=${GCS_BUCKET}`;

const formatTime = (seconds) => {
  const mins = Math.floor(seconds / 60);
  const secs = Math.floor(seconds % 60);
  return `${mins}:${secs.toString().padStart(2, '0')}`;
};

const getRiskColor = (level) => {
  const l = level?.toLowerCase();
  if (l === 'high') return { bg: 'bg-red-500/20', text: 'text-red-400', border: 'border-red-500/30' };
  if (l === 'moderate') return { bg: 'bg-yellow-500/20', text: 'text-yellow-400', border: 'border-yellow-500/30' };
  if (l === 'low') return { bg: 'bg-green-500/20', text: 'text-green-400', border: 'border-green-500/30' };
  return { bg: 'bg-zinc-500/20', text: 'text-zinc-400', border: 'border-zinc-500/30' };
};

// ============================================================================
// COMPONENTS
// ============================================================================

// Featured Widget Context - manages which widget is "popped up"
const FeaturedContext = createContext({ featuredId: null, setFeaturedId: () => {} });

// Widget Container with featured popup support
const Widget = ({ id, title, icon: Icon, iconColor, actions, children, className = '' }) => {
  const { featuredId, setFeaturedId } = useContext(FeaturedContext);
  const isFeatured = featuredId === id;

  return (
    <>
      {/* Normal in-flow widget */}
      <div className={`relative overflow-hidden bg-zinc-950/80 backdrop-blur-xl border border-white/[0.10] rounded-2xl shadow-[0_10px_28px_rgba(15,23,42,0.28)] flex flex-col ${className} ${isFeatured ? 'invisible' : ''}`}>
        <div className="pointer-events-none absolute inset-x-0 top-0 h-16 bg-gradient-to-b from-white/[0.08] to-transparent" />
        <div className="relative flex-shrink-0 px-3 py-2.5 border-b border-white/[0.08] flex items-center gap-2.5">
          <div className={`p-1.5 rounded-lg ring-1 ring-inset ring-white/14 ${iconColor}`}>
            <Icon size={14} />
          </div>
          <span className="text-sm font-semibold text-white/92 tracking-tight flex-1">{title}</span>
          {actions}
          <button
            onClick={() => setFeaturedId(id)}
            className="p-1.5 hover:bg-white/10 rounded-lg transition-colors text-white/40 hover:text-white/80"
            title="Focus view"
          >
            <Maximize2 size={13} />
          </button>
        </div>
        <div className="relative flex-1 overflow-auto">{children}</div>
      </div>

      {/* Featured popup overlay */}
      {isFeatured && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center p-[5vh_5vw]" onClick={() => setFeaturedId(null)}>
          {/* Glassmorphism backdrop */}
          <div className="absolute inset-0 bg-black/80 backdrop-blur-md" />

          {/* Featured widget - ~80% viewport */}
          <div
            className="relative w-full h-full max-w-[90vw] max-h-[85vh] overflow-hidden bg-zinc-950/95 backdrop-blur-2xl border border-amber-500/30 rounded-2xl shadow-[0_20px_60px_rgba(245,158,11,0.15),0_0_120px_rgba(245,158,11,0.05)] flex flex-col animate-in zoom-in-95 fade-in duration-200"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="pointer-events-none absolute inset-x-0 top-0 h-20 bg-gradient-to-b from-amber-500/[0.04] to-transparent" />
            <div className="relative flex-shrink-0 px-5 py-3 border-b border-amber-500/20 flex items-center gap-3">
              <div className={`p-2 rounded-lg ring-1 ring-inset ring-amber-500/30 ${iconColor}`}>
                <Icon size={18} />
              </div>
              <span className="text-base font-semibold text-white/95 tracking-tight flex-1">{title}</span>
              {actions}
              <button
                onClick={() => setFeaturedId(null)}
                className="p-2 hover:bg-white/10 rounded-lg transition-colors text-white/60 hover:text-white/90"
                title="Close focus view"
              >
                <X size={16} />
              </button>
            </div>
            <div className="relative flex-1 overflow-auto">{children}</div>
          </div>
        </div>
      )}
    </>
  );
};

// Timeline Scrubber with Analysis Markers
const TimelineScrubber = ({
  currentTime,
  duration,
  segments,
  analysisCache,
  onSeek,
  onSegmentChange,
  currentSegment
}) => {
  const containerRef = useRef(null);

  const handleClick = (e) => {
    if (!containerRef.current) return;
    const rect = containerRef.current.getBoundingClientRect();
    const x = e.clientX - rect.left;
    const percent = x / rect.width;
    // Calculate which segment was clicked
    const clickedSegment = Math.floor(percent * segments);
    if (onSegmentChange && clickedSegment >= 0 && clickedSegment < segments) {
      onSegmentChange(clickedSegment);
    }
  };

  // Generate segment markers with risk colors
  const markers = useMemo(() => {
    return Array.from({ length: segments }, (_, i) => {
      const cached = analysisCache[i];
      const risk = cached?.biomech?.frame_risk_level || cached?.biomech?.risk_level;
      const color = getRiskColor(risk);
      const startPercent = (i * SEGMENT_DURATION / duration) * 100;
      const widthPercent = (SEGMENT_DURATION / duration) * 100;
      return { index: i, startPercent, widthPercent, risk, color, hasData: !!cached };
    });
  }, [segments, duration, analysisCache]);

  return (
    <div className="flex-shrink-0 px-0.5 py-0.5 bg-zinc-950/50">
      {/* Main Timeline */}
      <div
        ref={containerRef}
        onClick={handleClick}
        className="relative h-4 bg-zinc-800/50 rounded-sm cursor-pointer overflow-hidden group"
      >
        {/* Segment Markers */}
        <div className="absolute inset-0 flex">
          {markers.map((m) => (
            <div
              key={m.index}
              className={`h-full border-r border-zinc-700/30 transition-all ${
                m.index === currentSegment ? 'bg-amber-500/30' : m.hasData ? m.color.bg : 'bg-zinc-800/30'
              } hover:brightness-125`}
              style={{ width: `${m.widthPercent}%` }}
              title={`Segment ${m.index + 1}${m.risk ? ` - ${m.risk} risk` : ''}`}
            />
          ))}
        </div>

        {/* Progress within current segment - shows as a fill within the highlighted segment */}
        <div
          className="absolute top-0 h-full bg-gradient-to-r from-amber-500/50 to-orange-500/50 pointer-events-none transition-all"
          style={{
            left: `${(currentSegment * SEGMENT_DURATION / duration) * 100}%`,
            width: `${((currentTime / SEGMENT_DURATION) * (SEGMENT_DURATION / duration)) * 100}%`
          }}
        />

        {/* Current Segment Indicator - vertical line at playhead position */}
        <div
          className="absolute top-0 h-full w-0.5 bg-amber-400 shadow-lg shadow-amber-400/50 pointer-events-none"
          style={{ left: `${((currentSegment * SEGMENT_DURATION + currentTime) / duration) * 100}%` }}
        />

        {/* Risk Legend (on hover — floats above the bar) */}
        <div className="absolute -top-5 right-1 flex gap-2 text-[9px] font-medium opacity-0 group-hover:opacity-100 transition-opacity bg-zinc-900/90 px-1.5 py-0.5 rounded shadow-lg">
          <span className="flex items-center gap-0.5"><span className="w-1.5 h-1.5 rounded-sm bg-green-500/60" />Low</span>
          <span className="flex items-center gap-0.5"><span className="w-1.5 h-1.5 rounded-sm bg-yellow-500/60" />Med</span>
          <span className="flex items-center gap-0.5"><span className="w-1.5 h-1.5 rounded-sm bg-red-500/60" />High</span>
        </div>
      </div>
    </div>
  );
};

// Segment Snapshot - compact metric cards for the viz quadrant
const SegmentSnapshot = ({ analysis, segmentIndex }) => {
  if (!analysis) {
    return (
      <div className="flex items-center justify-center py-3 text-white/30 text-xs">
        <Eye size={14} className="mr-1.5 opacity-50" /> No analysis for segment {segmentIndex + 1}
      </div>
    );
  }

  const t = analysis.tactical || {};
  const b = analysis.biomech || {};
  const c = analysis.cultural || {};
  const a = analysis.audio || {};
  const cs = analysis.biomech_catch_and_shoot || {};

  const riskLevel = b.frame_risk_level || b.risk_level || null;
  const riskColor = getRiskColor(riskLevel);
  const benchEnergy = typeof a.bench_energy === 'number' ? a.bench_energy : null;
  const shotCount = cs.segment_summary?.total_catch_and_shoot_3s || cs.attempts?.length || 0;

  return (
    <div className="space-y-1.5 mb-2">
      {/* Metric cards row */}
      <div className="grid grid-cols-4 gap-1">
        {/* Tactical */}
        <div className="bg-amber-500/10 border border-amber-500/20 rounded-lg p-1.5 text-center">
          <Target size={10} className="text-amber-400 mx-auto mb-0.5" />
          <div className="text-[9px] text-white/40 uppercase">Play</div>
          <div className="text-[10px] text-amber-300 font-medium truncate" title={t.play_type || 'N/A'}>
            {t.play_type ? (t.play_type.length > 12 ? t.play_type.substring(0, 12) + '...' : t.play_type) : '—'}
          </div>
        </div>

        {/* Biomech Risk */}
        <div className={`${riskLevel ? riskColor.bg : 'bg-zinc-800/50'} border ${riskLevel ? riskColor.border : 'border-white/[0.06]'} rounded-lg p-1.5 text-center`}>
          <Shield size={10} className={`${riskLevel ? riskColor.text : 'text-white/30'} mx-auto mb-0.5`} />
          <div className="text-[9px] text-white/40 uppercase">Risk</div>
          <div className={`text-[10px] font-semibold uppercase ${riskLevel ? riskColor.text : 'text-white/30'}`}>
            {riskLevel || '—'}
          </div>
        </div>

        {/* Shooting */}
        <div className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-1.5 text-center">
          <Crosshair size={10} className="text-orange-400 mx-auto mb-0.5" />
          <div className="text-[9px] text-white/40 uppercase">C&S 3s</div>
          <div className="text-[10px] text-orange-300 font-semibold">
            {shotCount > 0 ? shotCount : '—'}
          </div>
        </div>

        {/* Energy */}
        <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-lg p-1.5 text-center">
          <Zap size={10} className="text-cyan-400 mx-auto mb-0.5" />
          <div className="text-[9px] text-white/40 uppercase">Energy</div>
          <div className={`text-[10px] font-semibold ${benchEnergy != null ? (benchEnergy >= 7 ? 'text-red-400' : benchEnergy >= 4 ? 'text-amber-400' : 'text-green-400') : 'text-white/30'}`}>
            {benchEnergy != null ? `${benchEnergy}/10` : '—'}
          </div>
        </div>
      </div>

      {/* Key insight if available */}
      {t.key_insight && (
        <div className="bg-amber-500/8 border border-amber-500/15 rounded-lg px-2 py-1">
          <p className="text-[10px] text-amber-200/70 italic leading-relaxed truncate" title={t.key_insight}>
            {t.key_insight}
          </p>
        </div>
      )}
    </div>
  );
};

// Analysis Panel (Tactical/Biomech/Cultural)
const AnalysisPanel = ({ analysis, isLoading }) => {
  const [activeTab, setActiveTab] = usePersistedState('slam_spursokc_tab', 'tactical');

  if (isLoading) {
    return (
      <div className="flex items-center justify-center h-full">
        <Loader2 className="animate-spin text-amber-500" size={24} />
      </div>
    );
  }

  if (!analysis) {
    return (
      <div className="flex flex-col items-center justify-center h-full text-white/40 p-4">
        <Eye size={32} className="mb-2 opacity-50" />
        <p className="text-sm">No analysis for this segment</p>
      </div>
    );
  }

  const tabs = [
    { id: 'tactical', label: 'Tactical', icon: Target, color: 'text-amber-400' },
    { id: 'biomech', label: 'Biomech', icon: Activity, color: 'text-green-400' },
    { id: 'shooting', label: 'Shooting', icon: Crosshair, color: 'text-orange-400' },
    { id: 'cultural', label: 'Cultural', icon: Users, color: 'text-purple-400' },
    { id: 'audio', label: 'Audio', icon: Mic, color: 'text-cyan-400' },
    { id: 'cognitive', label: 'Cognitive', icon: Brain, color: 'text-amber-400' },
  ];

  const renderTactical = () => {
    const t = analysis.tactical || {};
    return (
      <div className="space-y-3.5 p-4">
        {t.play_type && (
          <div>
            <span className="text-xs text-white/40 uppercase tracking-wider">Play Type</span>
            <p className="text-sm text-white/90 mt-1 leading-relaxed">{t.play_type}</p>
          </div>
        )}
        {t.key_insight && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
            <span className="text-xs text-amber-400 uppercase tracking-wider">Key Insight</span>
            <p className="text-sm text-amber-100 mt-1 italic">"{t.key_insight}"</p>
          </div>
        )}
        {(Array.isArray(t.what_worked) && t.what_worked.length > 0) || (Array.isArray(t.what_didnt) && t.what_didnt.length > 0) ? (
          <div className="grid grid-cols-2 gap-3">
            {Array.isArray(t.what_worked) && t.what_worked.length > 0 && (
              <div>
                <span className="text-xs text-green-400 uppercase">What Worked</span>
                <ul className="mt-1 space-y-1">
                  {t.what_worked.map((item, i) => (
                    <li key={i} className="text-xs text-white/70 flex items-start gap-1">
                      <span className="text-green-400 mt-0.5">•</span> {typeof item === 'string' ? item : JSON.stringify(item)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
            {Array.isArray(t.what_didnt) && t.what_didnt.length > 0 && (
              <div>
                <span className="text-xs text-red-400 uppercase">What Didn't</span>
                <ul className="mt-1 space-y-1">
                  {t.what_didnt.map((item, i) => (
                    <li key={i} className="text-xs text-white/70 flex items-start gap-1">
                      <span className="text-red-400 mt-0.5">•</span> {typeof item === 'string' ? item : JSON.stringify(item)}
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        ) : null}
      </div>
    );
  };

  const renderBiomech = () => {
    const b = analysis.biomech || {};
    const risk = getRiskColor(b.frame_risk_level || b.risk_level);
    const obs = b.primary_observation || {};

    // Check if we have any meaningful biomech data (supports all schemas)
    const hasData = b.frame_risk_level || obs.player || b.measurements || b.recommendation ||
                    b.fatigue_signals || b.risk_level || b.concerns || b.concerning_movements || b.player_load;

    if (!hasData) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-white/40 p-4">
          <Activity size={24} className="mb-2 opacity-50" />
          <p className="text-xs">No biomech analysis for this segment</p>
        </div>
      );
    }

    // Detect schema type:
    // - New Agentic Vision: has concerning_movements, player_load (strings)
    // - Old Agentic: has frame_risk_level, primary_observation.player
    // - Legacy: has concerns, observations array
    const isNewAgenticSchema = b.concerning_movements || (b.risk_level && b.player_load && !obs.player);

    return (
      <div className="space-y-3.5 p-4">
        {/* Risk Level Badge - handles all schemas */}
        {(b.frame_risk_level || b.risk_level) && (
          <div className={`inline-flex items-center gap-2 px-3 py-2 rounded-xl ${risk.bg} ${risk.border} border`}>
            <Shield size={16} className={risk.text} />
            <span className={`text-sm font-semibold ${risk.text} uppercase`}>{b.frame_risk_level || b.risk_level} Risk</span>
          </div>
        )}

        {/* Concerning Movements (New Agentic Vision schema) */}
        {isNewAgenticSchema && b.concerning_movements && (
          <div className="bg-zinc-800/50 rounded-xl p-3 space-y-2">
            <span className="text-xs text-white/40 uppercase tracking-wider">Movement Analysis</span>
            <p className="text-sm text-white/80 mt-1 leading-relaxed">{b.concerning_movements}</p>
          </div>
        )}

        {/* Player Load (New Agentic Vision schema) */}
        {isNewAgenticSchema && b.player_load && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
            <span className="text-xs text-amber-400 uppercase">Player Load & Fatigue</span>
            <p className="text-sm text-amber-100/80 mt-1 leading-relaxed">{b.player_load}</p>
          </div>
        )}

        {/* Notable Athletes (New Agentic Vision schema) */}
        {isNewAgenticSchema && b.notable_athletes && (
          <div className="bg-green-500/10 border border-green-500/20 rounded-xl p-3">
            <span className="text-xs text-green-400 uppercase tracking-wider">Notable Athletes</span>
            <p className="text-sm text-white/80 mt-1 leading-relaxed">{b.notable_athletes}</p>
          </div>
        )}

        {/* Primary Observation (Old Agentic Vision schema) */}
        {!isNewAgenticSchema && obs.player && (
          <div className="bg-zinc-800/50 rounded-xl p-3 space-y-2">
            <div className="flex items-center gap-2">
              <span className="text-white font-medium">{obs.player}</span>
              {obs.action && <span className="text-white/50 text-sm">• {obs.action}</span>}
            </div>
            {obs.details && <p className="text-sm text-white/70">{obs.details}</p>}
            {obs.risk_type && (
              <span className={`inline-block text-xs px-2 py-1 rounded ${risk.bg} ${risk.text}`}>
                {obs.risk_type.replace(/_/g, ' ')}
              </span>
            )}
          </div>
        )}

        {/* Concerns (Legacy schema fallback) */}
        {!isNewAgenticSchema && !obs.player && b.concerns && (
          <div className="bg-zinc-800/50 rounded-xl p-3">
            <span className="text-xs text-white/40 uppercase tracking-wider">Analysis</span>
            <p className="text-sm text-white/70 mt-1">{b.concerns}</p>
          </div>
        )}

        {/* Measurements (Agentic Vision schema) */}
        {b.measurements && typeof b.measurements === 'object' && !Array.isArray(b.measurements) && Object.keys(b.measurements).length > 0 && (
          <div>
            <span className="text-xs text-white/40 uppercase tracking-wider">Measurements</span>
            <div className="grid grid-cols-3 gap-2 mt-2">
              {Object.entries(b.measurements).map(([key, value]) => (
                <div key={key} className="bg-zinc-800/50 rounded-lg p-2 text-center">
                  <div className="text-lg font-mono text-green-400">{typeof value === 'number' ? `${value}°` : value}</div>
                  <div className="text-[11px] text-white/40 uppercase">{key.replace(/_/g, ' ')}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Fatigue Signals - handles both string array and object array */}
        {Array.isArray(b.fatigue_signals) && b.fatigue_signals.length > 0 && (
          <div>
            <span className="text-xs text-white/40 uppercase tracking-wider">Fatigue Signals</span>
            <div className="space-y-2 mt-2">
              {b.fatigue_signals.map((signal, i) => (
                <div key={i} className="bg-orange-500/10 border border-orange-500/20 rounded-lg p-2">
                  {typeof signal === 'string' ? (
                    <p className="text-sm text-white/70">{signal}</p>
                  ) : (
                    <>
                      <div className="flex items-center justify-between">
                        <span className="text-sm text-white">{signal.player || 'Unknown'}</span>
                        <span className={`text-xs px-2 py-0.5 rounded ${
                          signal.level === 'high' ? 'bg-red-500/20 text-red-400' :
                          signal.level === 'moderate' ? 'bg-yellow-500/20 text-yellow-400' :
                          'bg-green-500/20 text-green-400'
                        }`}>{signal.level || 'unknown'}</span>
                      </div>
                      {signal.indicator && <p className="text-xs text-white/60 mt-1">{signal.indicator}</p>}
                    </>
                  )}
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Recommendation */}
        {b.recommendation && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
            <span className="text-xs text-amber-400 uppercase">Recommendation</span>
            <p className="text-sm text-amber-100/80 mt-1">{b.recommendation}</p>
          </div>
        )}
      </div>
    );
  };

  const renderShooting = () => {
    // v3 shooting lens (from pipeline v3)
    const shootingLens = analysis.shooting || {};
    const v3Shots = Array.isArray(shootingLens.shots) ? shootingLens.shots : [];

    // v2 biomech catch-and-shoot data
    const cs = analysis.biomech_catch_and_shoot || {};

    // If v3 shooting data exists, render it first
    if (v3Shots.length > 0 || shootingLens.shot_attempts > 0) {
      const shotSelColor = shootingLens.shot_selection_quality === 'Good' ? 'text-green-400 bg-green-500/15' :
        shootingLens.shot_selection_quality === 'Poor' ? 'text-red-400 bg-red-500/15' : 'text-amber-400 bg-amber-500/15';

      return (
        <div className="space-y-3 p-4">
          {/* Summary metrics */}
          <div className="flex items-center gap-2 text-xs flex-wrap">
            {shootingLens.shot_attempts > 0 && (
              <span className="px-2 py-1 bg-orange-500/15 text-orange-400 rounded-lg font-semibold">
                {shootingLens.shot_attempts} Shot{shootingLens.shot_attempts !== 1 ? 's' : ''}
              </span>
            )}
            {shootingLens.three_point_attempts > 0 && (
              <span className="px-2 py-1 bg-amber-500/15 text-amber-400 rounded-lg">
                {shootingLens.three_point_attempts} 3PT
              </span>
            )}
            {shootingLens.paint_touches > 0 && (
              <span className="px-2 py-1 bg-purple-500/15 text-purple-400 rounded-lg">
                {shootingLens.paint_touches} Paint
              </span>
            )}
            {shootingLens.shot_selection_quality && (
              <span className={`px-2 py-1 rounded-lg font-medium ${shotSelColor}`}>
                {shootingLens.shot_selection_quality}
              </span>
            )}
          </div>

          {/* Contest summary */}
          {shootingLens.contest_summary && (
            <div className="bg-orange-500/8 border border-orange-500/15 rounded-xl p-2.5">
              <span className="text-[10px] text-orange-400/60 uppercase tracking-wider">Contest</span>
              <p className="text-xs text-white/70 mt-0.5 leading-relaxed">{shootingLens.contest_summary}</p>
            </div>
          )}

          {/* Individual shot cards */}
          {v3Shots.map((shot, i) => {
            const isMade = shot.made;
            return (
              <div key={i} className={`rounded-xl border p-2.5 ${
                isMade ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'
              }`}>
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <span className={`text-xs font-bold ${isMade ? 'text-green-400' : 'text-red-400'}`}>
                      {isMade ? 'MAKE' : 'MISS'}
                    </span>
                    <span className="text-xs text-white/70">{shot.shooter}</span>
                  </div>
                  <div className="flex items-center gap-1.5 text-[10px] text-white/40">
                    {shot.zone && <span className="bg-white/[0.06] px-1.5 py-0.5 rounded">{shot.zone}</span>}
                    {shot.drive_type && <span className="bg-amber-500/15 text-amber-400 px-1.5 py-0.5 rounded">{shot.drive_type}</span>}
                  </div>
                </div>
                <div className="flex items-center gap-2 mt-1 text-[10px] text-white/50">
                  <span>{shot.type}</span>
                  {shot.distance_ft > 0 && <span>• {shot.distance_ft}ft</span>}
                  {shot.contested && <span className="text-red-400">• Contested</span>}
                </div>
                {shot.form_notes && (
                  <p className="text-[10px] text-white/40 italic mt-1">{shot.form_notes}</p>
                )}
              </div>
            );
          })}

          {/* Also show biomech C&S data if present */}
          {cs.attempts?.length > 0 && (
            <div className="mt-2 pt-2 border-t border-white/[0.06]">
              <span className="text-[10px] text-orange-400/60 uppercase tracking-wider">Biomech Catch & Shoot Detail</span>
            </div>
          )}
        </div>
      );
    }

    // Fallback: v2 biomech-only shooting display
    if (cs.no_catch_and_shoot) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-white/40 p-4">
          <Crosshair size={24} className="mb-2 opacity-50" />
          <p className="text-xs">No catch-and-shoot 3s in this segment</p>
          {cs.segment_note && <p className="text-[11px] text-white/30 mt-1 text-center max-w-[280px]">{cs.segment_note}</p>}
        </div>
      );
    }

    const attempts = cs.attempts || [];
    const summary = cs.segment_summary || {};

    if (attempts.length === 0) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-white/40 p-4">
          <Crosshair size={24} className="mb-2 opacity-50" />
          <p className="text-xs">{analysis.biomech_enhanced_at ? 'No catch-and-shoot 3s found' : 'Shooting analysis not yet available'}</p>
        </div>
      );
    }

    const scoreColor = (score) => {
      if (score >= 4) return 'text-green-400 bg-green-500/15';
      if (score >= 3) return 'text-amber-400 bg-amber-500/15';
      return 'text-red-400 bg-red-500/15';
    };

    const checkpointLabels = {
      foot_alignment: 'Foot Alignment',
      knee_bend_depth: 'Knee Bend',
      hip_shoulder_alignment: 'Hip-Shoulder',
      jump_timing_rhythm: 'Jump Timing',
      landing_drift: 'Landing Drift',
      shooting_pocket: 'Set Point',
      elbow_alignment: 'Elbow',
      guide_hand: 'Guide Hand',
      release_point_height: 'Release Point',
      wrist_snap_followthrough: 'Follow-Through',
      catch_readiness: 'Catch Ready',
      catch_to_release_time: 'Release Speed',
      center_of_gravity: 'COG',
      head_stability: 'Head Stable',
    };

    return (
      <div className="space-y-3 p-4">
        {/* Summary Bar */}
        {summary.total_catch_and_shoot_3s > 0 && (
          <div className="flex items-center gap-2 text-xs">
            <span className="px-2 py-1 bg-orange-500/15 text-orange-400 rounded-lg font-semibold">
              {summary.total_catch_and_shoot_3s} C&S 3PT
            </span>
            <span className="px-2 py-1 bg-green-500/15 text-green-400 rounded-lg">{summary.makes || 0} Made</span>
            <span className="px-2 py-1 bg-red-500/15 text-red-400 rounded-lg">{summary.misses || 0} Missed</span>
          </div>
        )}

        {/* Attempt Cards */}
        {attempts.map((attempt, i) => {
          const isMake = attempt.shot_result?.toLowerCase() === 'make';
          const cp = attempt.checkpoints || {};

          return (
            <div key={i} className={`rounded-xl border p-3 space-y-2.5 ${
              isMake ? 'bg-green-500/5 border-green-500/20' : 'bg-red-500/5 border-red-500/20'
            }`}>
              {/* Header */}
              <div className="flex items-center justify-between">
                <div className="flex items-center gap-2">
                  <span className={`text-sm font-bold ${isMake ? 'text-green-400' : 'text-red-400'}`}>
                    {isMake ? 'MAKE' : 'MISS'}
                  </span>
                  <span className="text-xs text-white/70">{attempt.shooter}</span>
                </div>
                <div className="flex items-center gap-1.5 text-[11px] text-white/40">
                  {attempt.quarter && <span>{attempt.quarter}</span>}
                  {attempt.approximate_timestamp && <span className="font-mono">{attempt.approximate_timestamp}</span>}
                </div>
              </div>

              {/* Checkpoint Score Grid */}
              <div className="grid grid-cols-7 gap-1">
                {Object.entries(checkpointLabels).map(([key, label]) => {
                  const checkpoint = cp[key];
                  if (!checkpoint) return null;
                  const score = checkpoint.score;
                  return (
                    <div key={key} className="text-center group relative" title={`${label}: ${checkpoint.observation || ''}`}>
                      <div className={`text-xs font-mono font-bold rounded-md py-1 ${scoreColor(score)}`}>
                        {score}
                      </div>
                      <div className="text-[8px] text-white/30 mt-0.5 truncate">{label}</div>
                    </div>
                  );
                })}
              </div>

              {/* Primary Note */}
              {attempt.primary_mechanical_note && (
                <p className="text-xs text-white/60 italic leading-relaxed">"{attempt.primary_mechanical_note}"</p>
              )}

              {/* Comparison Flag (misses only) */}
              {!isMake && attempt.comparison_flag && (
                <div className="bg-red-500/10 border border-red-500/20 rounded-lg p-2">
                  <span className="text-[10px] text-red-400 uppercase font-semibold">Likely Cause</span>
                  <p className="text-xs text-white/70 mt-0.5">{attempt.comparison_flag}</p>
                </div>
              )}
            </div>
          );
        })}
      </div>
    );
  };

  const renderCultural = () => {
    const c = analysis.cultural || {};
    const obs = c.primary_observation || {};
    const teamState = c.our_team_state || {};

    // Check schema types:
    // - Current pipeline (v3.0+): team_energy is numeric (1-10), body_language/leadership_moments are strings
    // - Old Agentic Vision: has overall_energy, primary_observation, our_team_state
    // - Legacy: has team_energy_communication or energy_communication
    const hasCurrentSchema = c.team_energy !== undefined && c.team_energy !== null && !c.overall_energy;
    const hasOldAgenticSchema = c.overall_energy || obs.type || teamState.confidence;
    const hasLegacySchema = c.team_energy_communication || c.energy_communication;

    // Check if we have any meaningful cultural data
    const hasData = hasCurrentSchema || hasOldAgenticSchema || hasLegacySchema || c.opponent_state || c.leadership_moments;

    if (!hasData) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-white/40 p-4">
          <Users size={24} className="mb-2 opacity-50" />
          <p className="text-xs">No cultural analysis for this segment</p>
        </div>
      );
    }

    // Current pipeline schema rendering (team_energy is numeric 1-10)
    if (hasCurrentSchema) {
      const energyScore = typeof c.team_energy === 'number' ? c.team_energy : null;
      const energyColor = energyScore !== null
        ? (energyScore >= 8 ? 'text-green-400' : energyScore >= 5 ? 'text-amber-400' : 'text-red-400')
        : 'text-white/80';

      return (
        <div className="space-y-3.5 p-4">
          {/* Team Energy — numeric score with color coding */}
          {c.team_energy !== undefined && (
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3">
              <span className="text-xs text-purple-400 uppercase tracking-wider">Team Energy</span>
              {energyScore !== null ? (
                <div className="flex items-center gap-2 mt-1">
                  <span className={`text-2xl font-bold ${energyColor}`}>{energyScore}</span>
                  <span className="text-xs text-white/40">/10</span>
                </div>
              ) : (
                <p className="text-sm text-white/80 mt-1 leading-relaxed">{c.team_energy}</p>
              )}
            </div>
          )}

          {/* Body Language */}
          {typeof c.body_language === 'string' && c.body_language && (
            <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-3">
              <span className="text-xs text-cyan-400 uppercase tracking-wider">Body Language</span>
              <p className="text-sm text-white/80 mt-1 leading-relaxed">{c.body_language}</p>
            </div>
          )}

          {/* Leadership Moments */}
          {typeof c.leadership_moments === 'string' && c.leadership_moments && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
              <span className="text-xs text-amber-400 uppercase tracking-wider">Leadership</span>
              <p className="text-sm text-white/80 mt-1 leading-relaxed">{c.leadership_moments}</p>
            </div>
          )}

          {/* Bench Reactions */}
          {typeof c.bench_reactions === 'string' && c.bench_reactions && (
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3">
              <span className="text-xs text-purple-400 uppercase tracking-wider">Bench Reactions</span>
              <p className="text-sm text-white/80 mt-1 leading-relaxed">{c.bench_reactions}</p>
            </div>
          )}

          {/* Huddle Dynamics */}
          {typeof c.huddle_dynamics === 'string' && c.huddle_dynamics && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
              <span className="text-xs text-amber-400 uppercase tracking-wider">Huddle Dynamics</span>
              <p className="text-sm text-white/80 mt-1 leading-relaxed">{c.huddle_dynamics}</p>
            </div>
          )}

          {/* Opponent State */}
          {typeof c.opponent_state === 'string' && c.opponent_state && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3">
              <span className="text-xs text-red-400 uppercase tracking-wider">Opponent State</span>
              <p className="text-sm text-white/80 mt-1 leading-relaxed">{c.opponent_state}</p>
            </div>
          )}
        </div>
      );
    }

    // Legacy schema rendering
    if (hasLegacySchema && !hasOldAgenticSchema) {
      return (
        <div className="space-y-3.5 p-4">
          {/* Team Energy & Communication */}
          {(c.team_energy_communication || c.energy_communication) && (
            <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3">
              <span className="text-xs text-purple-400 uppercase tracking-wider">Team Energy & Communication</span>
              <p className="text-sm text-white/80 mt-1">{c.team_energy_communication || c.energy_communication}</p>
            </div>
          )}

          {/* Leadership Moments (string format) */}
          {typeof c.leadership_moments === 'string' && c.leadership_moments && (
            <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
              <span className="text-xs text-amber-400 uppercase tracking-wider">Leadership</span>
              <p className="text-sm text-white/80 mt-1">{c.leadership_moments}</p>
            </div>
          )}

          {/* Opponent State (string format) */}
          {typeof c.opponent_state === 'string' && c.opponent_state && (
            <div className="bg-red-500/10 border border-red-500/20 rounded-xl p-3">
              <span className="text-xs text-red-400 uppercase tracking-wider">Opponent State</span>
              <p className="text-sm text-white/80 mt-1">{c.opponent_state}</p>
            </div>
          )}
        </div>
      );
    }

    // Agentic Vision schema rendering
    return (
      <div className="space-y-3.5 p-4">
        {/* Overall Energy */}
        {c.overall_energy && (
          <div className="flex items-center gap-3">
            <Heart size={18} className="text-purple-400" />
            <span className="text-sm font-medium capitalize">{c.overall_energy.replace(/_/g, ' ')}</span>
          </div>
        )}

        {/* Key Insight (if available) */}
        {c.key_insight && (
          <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3">
            <span className="text-xs text-purple-400 uppercase tracking-wider">Key Insight</span>
            <p className="text-sm text-purple-100 mt-1 italic">"{c.key_insight}"</p>
          </div>
        )}

        {/* Primary Observation (only if has content) */}
        {obs.type && (
          <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3">
            <span className="text-xs text-purple-400 uppercase">{obs.type.replace(/_/g, ' ')}</span>
            {obs.description && <p className="text-sm text-white/80 mt-1">{obs.description}</p>}
            {obs.significance && <p className="text-xs text-purple-300/70 mt-2 italic">{obs.significance}</p>}
          </div>
        )}

        {/* Team State Grid */}
        {(teamState.confidence || teamState.energy || teamState.communication || teamState.chemistry) && (
          <div className="grid grid-cols-2 gap-2">
            {teamState.confidence && (
              <div className="bg-zinc-800/50 rounded-xl p-3">
                <span className="text-xs text-white/40 uppercase">Confidence</span>
                <p className="text-sm font-semibold text-white capitalize mt-1">{teamState.confidence}</p>
              </div>
            )}
            {teamState.energy && (
              <div className="bg-zinc-800/50 rounded-xl p-3">
                <span className="text-xs text-white/40 uppercase">Energy</span>
                <p className="text-sm font-semibold text-white capitalize mt-1">{teamState.energy}</p>
              </div>
            )}
            {teamState.communication && (
              <div className="bg-zinc-800/50 rounded-xl p-3">
                <span className="text-xs text-white/40 uppercase">Communication</span>
                <p className="text-sm font-semibold text-white capitalize mt-1">{teamState.communication}</p>
              </div>
            )}
            {teamState.chemistry && (
              <div className="bg-zinc-800/50 rounded-xl p-3">
                <span className="text-xs text-white/40 uppercase">Chemistry</span>
                <p className="text-sm font-semibold text-white capitalize mt-1">{teamState.chemistry}</p>
              </div>
            )}
          </div>
        )}
      </div>
    );
  };

  const renderAudio = () => {
    const a = analysis.audio || {};

    const hasData = a.bench_energy || a.crowd_indicators || a.emotional_intensity ||
                    a.momentum_indicators || a.communication_observed || a.inferred_audio_events?.length;

    if (!hasData) {
      return (
        <div className="flex flex-col items-center justify-center h-32 text-white/40 p-4">
          <Mic size={24} className="mb-2 opacity-50" />
          <p className="text-xs">No audio analysis for this segment</p>
        </div>
      );
    }

    const energyColor = (val) => {
      if (val >= 8) return 'text-red-400';
      if (val >= 5) return 'text-amber-400';
      return 'text-green-400';
    };

    return (
      <div className="space-y-3.5 p-4">
        {/* Energy Gauges */}
        <div className="grid grid-cols-2 gap-2">
          {a.bench_energy != null && (
            <div className="bg-zinc-800/50 rounded-xl p-3">
              <span className="text-xs text-white/40 uppercase">Bench Energy</span>
              <div className="flex items-center gap-2 mt-1">
                <div className="flex-1 h-2 bg-zinc-700 rounded-full overflow-hidden">
                  <div className={`h-full rounded-full ${a.bench_energy >= 8 ? 'bg-red-500' : a.bench_energy >= 5 ? 'bg-amber-500' : 'bg-green-500'}`} style={{ width: `${(a.bench_energy / 10) * 100}%` }} />
                </div>
                <span className={`text-sm font-mono font-bold ${energyColor(a.bench_energy)}`}>{a.bench_energy}/10</span>
              </div>
            </div>
          )}
          {a.emotional_intensity && (
            <div className="bg-zinc-800/50 rounded-xl p-3">
              <span className="text-xs text-white/40 uppercase">Intensity</span>
              <p className="text-sm font-semibold text-cyan-400 mt-1 capitalize">{a.emotional_intensity}</p>
            </div>
          )}
        </div>

        {/* Crowd & Momentum */}
        {a.crowd_indicators && (
          <div className="bg-cyan-500/10 border border-cyan-500/20 rounded-xl p-3">
            <span className="text-xs text-cyan-400 uppercase tracking-wider">Crowd Indicators</span>
            <p className="text-sm text-white/80 mt-1 leading-relaxed">{a.crowd_indicators}</p>
          </div>
        )}

        {a.momentum_indicators && (
          <div className="bg-purple-500/10 border border-purple-500/20 rounded-xl p-3">
            <span className="text-xs text-purple-400 uppercase tracking-wider">Momentum</span>
            <p className="text-sm text-white/80 mt-1 leading-relaxed">{a.momentum_indicators}</p>
          </div>
        )}

        {a.communication_observed && (
          <div className="bg-amber-500/10 border border-amber-500/20 rounded-xl p-3">
            <span className="text-xs text-amber-400 uppercase tracking-wider">Communication</span>
            <p className="text-sm text-white/80 mt-1 leading-relaxed">{a.communication_observed}</p>
          </div>
        )}

        {/* Inferred Audio Events Timeline */}
        {Array.isArray(a.inferred_audio_events) && a.inferred_audio_events.length > 0 && (
          <div>
            <span className="text-xs text-white/40 uppercase tracking-wider">Audio Events Timeline</span>
            <div className="space-y-1.5 mt-2">
              {a.inferred_audio_events.map((event, i) => (
                <div key={i} className="flex items-start gap-2 bg-zinc-800/40 rounded-lg p-2 border border-white/[0.04]">
                  <div className={`flex-shrink-0 w-7 h-7 rounded-full flex items-center justify-center text-xs font-bold ${
                    event.type === 'coaching' ? 'bg-amber-500/20 text-amber-400' :
                    event.type === 'celebration' ? 'bg-green-500/20 text-green-400' :
                    event.type === 'frustration' ? 'bg-red-500/20 text-red-400' :
                    'bg-zinc-700 text-white/60'
                  }`}>
                    {event.intensity || '?'}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-center gap-2">
                      <span className="text-xs font-mono text-amber-400">{event.timestamp_approx}</span>
                      <span className="text-xs uppercase text-white/40">{event.type}</span>
                    </div>
                    <p className="text-xs text-white/70 mt-0.5 leading-relaxed">{event.description}</p>
                  </div>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  const renderCognitive = () => {
    const cog = analysis?.cognitive;
    if (!cog) return <div className="text-gray-500 text-sm p-4">No cognitive analysis available for this segment.</div>;

    return (
      <div className="space-y-4 p-2">
        {/* CS3 Score Header */}
        <div className="flex items-center gap-3">
          <div className={`text-3xl font-bold ${
            (cog.cs3_score || 0) >= 7 ? 'text-green-400' :
            (cog.cs3_score || 0) >= 5 ? 'text-amber-400' : 'text-red-400'
          }`}>
            {typeof cog.cs3_score === 'number' ? cog.cs3_score.toFixed(1) : (cog.cs3_score || 'N/A')}
          </div>
          <div>
            <div className="text-xs text-gray-400">CS3 Score</div>
            <div className="text-sm text-white capitalize">{cog.dominant_cognitive_state || 'Unknown'}</div>
          </div>
          <div className="ml-auto text-right">
            <div className="text-xs text-gray-400">Flow State</div>
            <div className="text-sm text-amber-300 capitalize">{cog.flow_state || '—'}</div>
          </div>
        </div>

        {/* Metrics Grid */}
        <div className="grid grid-cols-3 gap-2 text-xs">
          <div className="bg-[#1A1A1A]/60 p-2 rounded">
            <span className="text-gray-400">Composure</span>
            <div className="text-white font-medium">{cog.composure_rating || '—'}/10</div>
          </div>
          <div className="bg-[#1A1A1A]/60 p-2 rounded">
            <span className="text-gray-400">Decision Quality</span>
            <div className="text-white font-medium capitalize">{cog.decision_quality || '—'}</div>
          </div>
          <div className="bg-[#1A1A1A]/60 p-2 rounded">
            <span className="text-gray-400">Cognitive Load</span>
            <div className="text-white font-medium capitalize">{cog.cognitive_load || '—'}</div>
          </div>
        </div>

        {/* Key Cognitive Moment */}
        {cog.key_cognitive_moment && (
          <div className="bg-amber-900/20 border border-amber-700/30 p-3 rounded-lg">
            <div className="text-xs text-amber-400 mb-1 font-medium">Key Cognitive Moment</div>
            <div className="text-sm text-gray-200">{cog.key_cognitive_moment}</div>
          </div>
        )}

        {/* Momentum Psychology */}
        {cog.momentum_psychology && (
          <div className="bg-[#1A1A1A]/50 p-3 rounded-lg">
            <div className="text-xs text-gray-400 mb-2 font-medium">Momentum Psychology</div>
            <div className="grid grid-cols-2 gap-2 text-xs">
              <div>
                <span className="text-gray-500">Team Confidence:</span>{' '}
                <span className="text-white capitalize">{cog.momentum_psychology.team_confidence || '—'}</span>
              </div>
              <div>
                <span className="text-gray-500">Pressure Response:</span>{' '}
                <span className="text-white capitalize">{cog.momentum_psychology.pressure_response || '—'}</span>
              </div>
            </div>
            {cog.momentum_psychology.clutch_indicators && (
              <div className="text-xs text-gray-300 mt-1">{cog.momentum_psychology.clutch_indicators}</div>
            )}
          </div>
        )}

        {/* Hesitation Events */}
        {cog.hesitation_events?.length > 0 && (
          <div>
            <div className="text-xs text-gray-400 mb-2 font-medium">Hesitation Events</div>
            <div className="space-y-2">
              {cog.hesitation_events.map((evt, i) => (
                <div key={i} className={`p-2 rounded text-xs border ${
                  evt.impact === 'positive' ? 'bg-green-900/20 border-green-700/30' :
                  evt.impact === 'negative' ? 'bg-red-900/20 border-red-700/30' :
                  'bg-[#1A1A1A]/60 border-gray-700/30'
                }`}>
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-white font-medium">{evt.player}</span>
                    <span className="text-gray-400">{evt.timestamp_approx}</span>
                  </div>
                  <div className="text-gray-300">{evt.description}</div>
                  <div className="text-gray-500 mt-1 capitalize">{evt.type?.replace(/_/g, ' ')}</div>
                </div>
              ))}
            </div>
          </div>
        )}

        {/* Player Cognitive Profiles */}
        {cog.player_cognitive_profiles?.length > 0 && (
          <div>
            <div className="text-xs text-gray-400 mb-2 font-medium">Player Cognitive Profiles</div>
            <div className="space-y-2">
              {cog.player_cognitive_profiles.map((p, i) => (
                <div key={i} className="bg-[#1A1A1A]/50 p-2 rounded text-xs">
                  <div className="flex justify-between items-center mb-1">
                    <span className="text-white font-medium">{p.player}</span>
                    <div className="flex items-center gap-2">
                      <span className={`capitalize ${
                        p.state === 'flow' ? 'text-green-400' :
                        p.state === 'stressed' || p.state === 'hesitant' ? 'text-red-400' :
                        'text-amber-400'
                      }`}>{p.state}</span>
                      <span className={`font-mono font-bold ${
                        (p.cs3_estimate || 0) >= 7 ? 'text-green-400' :
                        (p.cs3_estimate || 0) >= 5 ? 'text-amber-400' : 'text-red-400'
                      }`}>{p.cs3_estimate?.toFixed(1)}</span>
                    </div>
                  </div>
                  <div className="text-gray-400">{p.evidence}</div>
                  {p.trend && <div className="text-gray-500 mt-1">Trend: <span className="capitalize">{p.trend}</span></div>}
                </div>
              ))}
            </div>
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="h-full flex flex-col min-h-0">
      {/* Tabs */}
      <div className="flex-shrink-0 flex border-b border-white/[0.06]">
        {tabs.map(tab => (
          <button
            key={tab.id}
            onClick={() => setActiveTab(tab.id)}
            className={`flex-1 px-3 py-2.5 flex items-center justify-center gap-2 text-xs font-semibold uppercase tracking-wide transition-all ${
              activeTab === tab.id
                ? `${tab.color} bg-white/[0.05] border-b-2 border-current`
                : 'text-white/40 hover:text-white/60 hover:bg-white/[0.03]'
            }`}
          >
            <tab.icon size={15} />
            {tab.label}
          </button>
        ))}
      </div>

      {/* Content */}
      <div className="flex-1 overflow-y-auto custom-scrollbar min-h-0">
        {activeTab === 'tactical' && renderTactical()}
        {activeTab === 'biomech' && renderBiomech()}
        {activeTab === 'shooting' && renderShooting()}
        {activeTab === 'cultural' && renderCultural()}
        {activeTab === 'audio' && renderAudio()}
        {activeTab === 'cognitive' && renderCognitive()}
      </div>
    </div>
  );
};

// Reasoning Trace — shows real-time thinking steps during streaming queries
const ReasoningTrace = ({ steps, isActive }) => {
  const [isCollapsed, setIsCollapsed] = useState(false);

  if (!steps || steps.length === 0) return null;

  const activeStep = steps.find(s => s.status === 'active');
  const totalDuration = steps.reduce((sum, s) => sum + (s.duration_ms || 0), 0);
  const completedSteps = steps.filter(s => s.status === 'complete').length;

  return (
    <div className="mb-2">
      <button
        onClick={() => setIsCollapsed(!isCollapsed)}
        className="flex items-center gap-2 w-full text-left px-2 py-1 rounded-lg hover:bg-white/[0.03] transition-all group"
      >
        <div className={`w-4 h-4 rounded-full flex items-center justify-center ${
          isActive ? 'bg-cyan-500/20 animate-pulse' : 'bg-emerald-500/20'
        }`}>
          {isActive ? (
            <Loader2 size={9} className="text-cyan-400 animate-spin" />
          ) : (
            <Check size={9} className="text-emerald-400" />
          )}
        </div>
        <span className="text-[10px] text-white/50 font-medium">
          {isActive
            ? `Thinking... ${activeStep?.label || ''}`
            : `${completedSteps} steps completed`
          }
        </span>
        {totalDuration > 0 && (
          <span className="text-[9px] text-white/30 ml-auto flex items-center gap-0.5">
            <Clock size={8} /> {(totalDuration / 1000).toFixed(1)}s
          </span>
        )}
        <ChevronDown size={10} className={`text-white/30 transition-transform ${isCollapsed ? '' : 'rotate-180'}`} />
      </button>

      {!isCollapsed && (
        <div className="mt-1 ml-3 pl-3 border-l border-white/[0.06] space-y-1">
          {steps.map((step, i) => (
            <div key={step.id || i} className="flex items-center gap-2 py-0.5">
              <div className={`w-3 h-3 rounded-full flex items-center justify-center flex-shrink-0 ${
                step.status === 'complete' ? 'bg-emerald-500/20' :
                step.status === 'active' ? 'bg-cyan-500/20' :
                'bg-zinc-700/50'
              }`}>
                {step.status === 'complete' ? (
                  <Check size={7} className="text-emerald-400" />
                ) : step.status === 'active' ? (
                  <Loader2 size={7} className="text-cyan-400 animate-spin" />
                ) : (
                  <div className="w-1.5 h-1.5 rounded-full bg-zinc-500" />
                )}
              </div>
              <span className={`text-[10px] ${
                step.status === 'active' ? 'text-cyan-400' :
                step.status === 'complete' ? 'text-white/50' :
                'text-white/30'
              }`}>
                {step.type === 'tool_call' ? (
                  <span className="flex items-center gap-1">
                    <Database size={8} className="text-amber-400/60" />
                    {step.label}
                    {step.rows && <span className="text-amber-400/40">({step.rows} rows)</span>}
                  </span>
                ) : (
                  step.label
                )}
              </span>
              {step.duration_ms && (
                <span className="text-[9px] text-white/20 ml-auto">{(step.duration_ms / 1000).toFixed(1)}s</span>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
};

// Chat Interface with Context Hierarchy: Segment → Game → League
const ChatInterface = ({ analysis, gameContext, currentSegment, allSegmentAnalyses, isAutoLoadingSegments, onNavigateSegment }) => {
  const [messages, setMessages] = useState(() => {
    try {
      const saved = localStorage.getItem('slam_spursokc_chatHistory');
      return saved ? JSON.parse(saved) : [];
    } catch { return []; }
  });
  const [input, setInput] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [isReanalyzing, setIsReanalyzing] = useState(false); // Video re-analysis in progress
  const [contextLevel, setContextLevel] = usePersistedState('slam_spursokc_chatScope', 'segment'); // 'segment' | 'game' | 'league'

  // Persist chat messages (max 50 to stay under localStorage limits)
  useEffect(() => {
    try {
      const toSave = messages.slice(-50);
      localStorage.setItem('slam_spursokc_chatHistory', JSON.stringify(toSave));
    } catch { /* localStorage full or unavailable */ }
  }, [messages]);
  const [reasoningSteps, setReasoningSteps] = useState([]); // Streaming reasoning trace
  const [synergySegmentData, setSynergySegmentData] = useState(null);
  const [synergyGameData, setSynergyGameData] = useState(null);
  const [priorSegmentAnalyses, setPriorSegmentAnalyses] = useState([]);
  const messagesEndRef = useRef(null);
  const activeGameId = gameContext?.id || null;

  const totalSegments = gameContext?.segments || 0;
  const loadedSegments = Object.keys(allSegmentAnalyses || {}).length;

  useEffect(() => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  // Reset messages and Synergy data when game changes
  useEffect(() => {
    setMessages([]);
    setContextLevel('segment');
    setSynergySegmentData(null);
    setSynergyGameData(null);
    setPriorSegmentAnalyses([]);
  }, [gameContext?.id]);

  // Fetch prior user analyses for current segment (persistent learning)
  useEffect(() => {
    if (!activeGameId) return;
    const controller = new AbortController();

    const fetchPriorAnalyses = async () => {
      try {
        const response = await fetch(
          `/api/basketball/segment-analyses/${encodeURIComponent(activeGameId)}/${currentSegment}`,
          { signal: controller.signal }
        );
        if (response.ok) {
          const data = await response.json();
          if (!controller.signal.aborted && data?.success) {
            setPriorSegmentAnalyses(data.analyses || []);
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.log('No prior analyses:', err.message);
        }
      }
    };

    fetchPriorAnalyses();
    return () => controller.abort();
  }, [activeGameId, currentSegment]);

  // Fetch Synergy play-by-play data for current segment
  useEffect(() => {
    if (!activeGameId) return;
    const controller = new AbortController();

    const fetchSegmentSynergy = async () => {
      try {
        const response = await fetch(`/api/synergy/${encodeURIComponent(activeGameId)}/segment/${currentSegment}`, {
          signal: controller.signal,
        });
        if (response.ok) {
          const data = await response.json();
          if (!controller.signal.aborted && data?.gameId === activeGameId) {
            setSynergySegmentData(data);
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error fetching Synergy segment data:', err);
        }
      }
    };

    fetchSegmentSynergy();
    return () => controller.abort();
  }, [activeGameId, currentSegment]);

  // Fetch Synergy game-level data when switching to game context
  useEffect(() => {
    if (!activeGameId || contextLevel !== 'game') return;
    if (synergyGameData?.gameId === activeGameId) return;
    const controller = new AbortController();

    const fetchGameSynergy = async () => {
      try {
        const response = await fetch(`/api/synergy/${encodeURIComponent(activeGameId)}/game`, {
          signal: controller.signal,
        });
        if (response.ok) {
          const data = await response.json();
          if (!controller.signal.aborted && data?.gameId === activeGameId) {
            setSynergyGameData(data);
          }
        }
      } catch (err) {
        if (err.name !== 'AbortError') {
          console.error('Error fetching Synergy game data:', err);
        }
      }
    };

    fetchGameSynergy();
    return () => controller.abort();
  }, [activeGameId, contextLevel, synergyGameData?.gameId]);

  // Load all segments when switching to game context
  // Build context based on hierarchy level
  const buildContext = () => {
    if (contextLevel === 'segment') {
      // Segment-level: Only current segment data + Synergy play-by-play
      if (!analysis) return 'ERROR: No analysis data loaded for this segment.';

      // Include Synergy play-by-play if available
      const synergyContext = synergySegmentData?.context || 'Play-by-play data loading...';

      // Format audio analysis if present
      const audioContext = analysis.audio ? `
## AUDIO ANALYSIS
- Crowd Energy: ${analysis.audio.crowd_indicators || 'N/A'}
- Bench Energy: ${analysis.audio.bench_energy || 'N/A'}/10
- Emotional Intensity: ${analysis.audio.emotional_intensity || 'N/A'}
- Momentum: ${analysis.audio.momentum_indicators || 'N/A'}
- Communication Observed: ${analysis.audio.communication_observed || 'N/A'}
${analysis.audio.inferred_audio_events?.length ? `### Key Audio Events:\n${analysis.audio.inferred_audio_events.map(e => `- [${e.timestamp_approx}] ${e.type}: ${e.description} (intensity: ${e.intensity}/10)`).join('\n')}` : ''}` : '';

      // Include prior user analyses for persistent learning
      const priorContext = priorSegmentAnalyses.length > 0
        ? `\n\n## PRIOR USER ANALYSES (Accumulated Knowledge)\n${priorSegmentAnalyses.map((a, i) => `### Q&A #${i + 1} (${new Date(a.timestamp).toLocaleString()})\n**Q:** ${a.question}\n**A:** ${a.answer}\n`).join('\n')}`
        : '';

      return `
CONTEXT LEVEL: SEGMENT (${currentSegment + 1} of ${gameContext?.segments})
GAME: ${gameContext?.name} - ${gameContext?.date}
GAME ID: ${gameContext?.id}
TIME: ${formatTime((analysis.segment?.start_ms || 0) / 1000)} - ${formatTime((analysis.segment?.end_ms || 0) / 1000)}

## SYNERGY PLAY-BY-PLAY
${synergyContext}
${audioContext}

## AI VIDEO ANALYSIS
TACTICAL: ${JSON.stringify(analysis.tactical || {}, null, 2)}
BIOMECH: ${JSON.stringify(analysis.biomech || {}, null, 2)}
SHOOTING (Catch-and-Shoot 3PT Biomechanics): ${JSON.stringify(analysis.biomech_catch_and_shoot || {}, null, 2)}
CULTURAL: ${JSON.stringify(analysis.cultural || {}, null, 2)}
COGNITIVE (CS3 - Cognitive State Scores): ${JSON.stringify(analysis.cognitive || {}, null, 2)}
INSIGHTS: ${JSON.stringify(analysis.integrated_insights || [], null, 2)}
ALERTS: ${JSON.stringify(analysis.priority_alerts || [], null, 2)}
${priorContext}`;
    }

    if (contextLevel === 'game') {
      // Game-level: Aggregated data across all analyzed segments
      const analyzedSegments = allSegmentAnalyses || {};
      const segmentCount = Object.keys(analyzedSegments).length;

      // Aggregate key insights across segments
      const allAlerts = [];
      const riskSummary = { high: 0, moderate: 0, low: 0 };
      const players = new Set();
      const playTypes = {};
      const tacticalPatterns = [];
      const cognitiveScores = [];
      const cognitiveStates = {};

      Object.entries(analyzedSegments).forEach(([segIdx, seg]) => {
        if (seg?.priority_alerts) allAlerts.push(...seg.priority_alerts.map(a => ({ ...a, segment: parseInt(segIdx) + 1 })));
        const risk = seg?.biomech?.frame_risk_level?.toLowerCase() || seg?.biomech?.risk_level?.toLowerCase();
        if (risk) riskSummary[risk] = (riskSummary[risk] || 0) + 1;
        if (seg?.biomech?.primary_observation?.player) players.add(seg.biomech.primary_observation.player);

        // Track tactical patterns
        const playType = seg?.tactical?.play_type || seg?.tactical?.primary_action;
        if (playType) playTypes[playType] = (playTypes[playType] || 0) + 1;

        // Collect tactical insights
        if (seg?.tactical?.key_insight || seg?.tactical?.summary) {
          tacticalPatterns.push({
            segment: parseInt(segIdx) + 1,
            insight: seg?.tactical?.key_insight || seg?.tactical?.summary
          });
        }

        // Aggregate cognitive/CS3 data
        if (seg?.cognitive?.cs3_score) {
          cognitiveScores.push(seg.cognitive.cs3_score);
          const state = seg.cognitive.dominant_cognitive_state;
          if (state) cognitiveStates[state] = (cognitiveStates[state] || 0) + 1;
        }
      });

      // Sort play types by frequency
      const topPlayTypes = Object.entries(playTypes)
        .sort((a, b) => b[1] - a[1])
        .slice(0, 5)
        .map(([type, count]) => `${type}: ${count}x`);

      // Include Synergy game data if available
      const synergyContext = synergyGameData?.context || 'Synergy play-by-play data loading...';

      return `
CONTEXT LEVEL: FULL GAME ANALYSIS
GAME: ${gameContext?.name} - ${gameContext?.date}
GAME ID: ${gameContext?.id}
COVERAGE: ${segmentCount} of ${totalSegments} segments analyzed (${Math.round(segmentCount/totalSegments*100)}%)

${synergyContext}

## AI VIDEO ANALYSIS SUMMARY

### Biomechanical Risk Distribution:
- High Risk Segments: ${riskSummary.high}
- Moderate Risk: ${riskSummary.moderate}
- Low Risk: ${riskSummary.low}

### Players with Notable Observations:
${Array.from(players).join(', ') || 'No specific players flagged'}

### Most Common Play Types (from video):
${topPlayTypes.join('\n') || 'No play types identified'}

## PRIORITY ALERTS (Top 10 across game):
${allAlerts.slice(0, 10).map(a => `- [Seg ${a.segment}] ${a.type || a.category}: ${a.message || a.description}`).join('\n') || 'No alerts'}

## TACTICAL PATTERNS OBSERVED:
${tacticalPatterns.slice(0, 8).map(p => `- Segment ${p.segment}: ${p.insight}`).join('\n') || 'No patterns collected yet'}

## COGNITIVE STATE (CS3) SUMMARY:
${cognitiveScores.length > 0 ? `- Average CS3 Score: ${(cognitiveScores.reduce((a, b) => a + b, 0) / cognitiveScores.length).toFixed(1)}/10
- Segments with CS3 data: ${cognitiveScores.length}
- Dominant States: ${Object.entries(cognitiveStates).sort((a, b) => b[1] - a[1]).map(([s, c]) => s + ': ' + c + 'x').join(', ')}` : 'No cognitive data available'}

## CURRENT SEGMENT FOR REFERENCE:
Segment ${currentSegment + 1}: ${analysis?.tactical?.play_type || 'Unknown play'}, Risk: ${analysis?.biomech?.frame_risk_level || analysis?.biomech?.risk_level || 'Unknown'}`;
    }

    // League level - uses full basketball data APIs via function calling
    return `
CONTEXT LEVEL: LEAGUE (NBA)
Currently viewing: ${gameContext?.name} - ${gameContext?.date}
Current game_id in review: ${gameContext?.id}
Segment: ${currentSegment + 1} of ${gameContext?.segments}

You have access to comprehensive basketball data through function calling tools:
- NL2SQL for BigQuery analytics (shots, possessions, picks, box scores, player stats)
- NatStat database (2051 teams, 2449 players, 5900 games across 30 leagues)
- Box scores (points, rebounds, assists, free throws for NBA games)
- Shot quality analysis (expected PPP by zone and type)
- Vertex AI Search (scouting reports, knowledge base)
- Video analysis data (507 segments, 4 Spurs vs OKC games)

Use these tools to answer questions about league-wide trends, player comparisons, team analytics, and basketball intelligence.`;
  };

  // Parse segment references in AI responses and make them clickable
  const renderSegmentLinks = (text) => {
    if (!onNavigateSegment || !text) return text;
    // Match patterns like [Segment 47 (12:30)] or segment://47
    return text.replace(/\[Segment (\d+)(?:\s*\(([^)]+)\))?\]/g, (match, segNum) => {
      return match; // Keep original — MarkdownMessage handles rendering
    });
  };

  // SSE streaming handler for game/league modes
  const sendStreamingMessage = async (userMessage, requestBody) => {
    setReasoningSteps([]);
    const controller = new AbortController();

    try {
      const response = await fetch('/api/chat/context/stream', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(requestBody),
        signal: controller.signal,
      });

      if (!response.ok) {
        throw new Error(`Stream error: ${response.status}`);
      }

      const reader = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';
      let finalContent = '';
      let finalMetadata = {};

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;

        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() || '';

        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try {
            const event = JSON.parse(line.slice(6));

            switch (event.type) {
              case 'thinking':
                setReasoningSteps(prev => {
                  const existing = prev.findIndex(s => s.id === event.step);
                  if (existing >= 0) {
                    const updated = [...prev];
                    updated[existing] = { ...updated[existing], ...event, id: event.step };
                    return updated;
                  }
                  return [...prev, { ...event, id: event.step }];
                });
                break;

              case 'tool_call':
                setReasoningSteps(prev => {
                  const toolId = `tool_${event.name}_${Date.now()}`;
                  if (event.status === 'started') {
                    return [...prev, { ...event, id: toolId, type: 'tool_call' }];
                  }
                  // Update existing tool call
                  const idx = [...prev].reverse().findIndex(s => s.type === 'tool_call' && s.name === event.name && s.status === 'started');
                  if (idx >= 0) {
                    const realIdx = prev.length - 1 - idx;
                    const updated = [...prev];
                    updated[realIdx] = { ...updated[realIdx], ...event };
                    return updated;
                  }
                  return [...prev, { ...event, id: toolId, type: 'tool_call' }];
                });
                break;

              case 'content':
                finalContent = event.text || '';
                break;

              case 'done':
                finalMetadata = event;
                break;

              case 'error':
                throw new Error(event.message || 'Stream error');
            }
          } catch (parseErr) {
            if (parseErr.message?.includes('Stream error')) throw parseErr;
            // Skip unparseable SSE lines
          }
        }
      }

      return {
        content: finalContent,
        source: contextLevel,
        toolCalls: finalMetadata.toolCalls,
        orchestration: finalMetadata.orchestration,
      };
    } catch (err) {
      if (err.name === 'AbortError') return null;
      throw err;
    }
  };

  const sendMessage = async () => {
    if (!input.trim() || isLoading) return;

    const userMessage = input.trim();
    const userMsg = { role: 'user', content: userMessage };
    setMessages(prev => [...prev, userMsg]);
    setInput('');
    setIsLoading(true);
    setReasoningSteps([]);

    try {
      const context = buildContext();
      let data;

      if (contextLevel === 'segment') {
        // SEGMENT MODE: On-demand video re-analysis via Gemini 3 Flash agentic vision
        setIsReanalyzing(true);
        try {
          const response = await fetch('/api/basketball/segment-analyze', {
            method: 'POST',
            headers: { 'Content-Type': 'application/json' },
            body: JSON.stringify({
              game_id: activeGameId,
              segment_index: currentSegment,
              user_question: userMessage,
              existing_analysis: analysis ? {
                tactical: analysis.tactical,
                biomech: analysis.biomech,
                cultural: analysis.cultural,
                audio: analysis.audio,
                cognitive: analysis.cognitive,
              } : null,
              include_prior: true,
              // Include game-wide segment summaries for cross-referencing
              game_segment_summaries: Object.entries(allSegmentAnalyses || {}).slice(0, 50).map(([idx, seg]) => ({
                segment: parseInt(idx),
                play_type: seg?.tactical?.play_type || seg?.tactical?.primary_action || null,
                insight: seg?.tactical?.key_insight || seg?.tactical?.summary || null,
                risk: seg?.biomech?.frame_risk_level || seg?.biomech?.risk_level || null,
                cs3_score: seg?.cognitive?.cs3_score || null,
                cognitive_state: seg?.cognitive?.dominant_cognitive_state || null,
              })).filter(s => s.play_type || s.insight),
            })
          });
          const rawResp = await response.json();
          // respond() wrapper puts actual payload inside rawResp.data
          const segData = rawResp.data || rawResp;

          if (segData.analysis_id) {
            setPriorSegmentAnalyses(prev => [{
              id: segData.analysis_id,
              question: userMessage,
              answer: segData.analysis,
              timestamp: new Date().toISOString(),
            }, ...prev]);
          }

          const errorMsg = typeof rawResp.error === 'string' ? rawResp.error : rawResp.error?.message;
          data = {
            content: segData.analysis || errorMsg || 'Analysis could not be completed.',
            source: 'segment-reanalysis',
            analysis_id: segData.analysis_id,
          };
        } finally {
          setIsReanalyzing(false);
        }
      } else if (contextLevel === 'game') {
        // GAME MODE: SSE streaming with reasoning trace
        data = await sendStreamingMessage(userMessage, {
          message: userMessage,
          history: messages.slice(-6).map(m => ({ role: m.role, content: m.content })),
          sport: 'NBA',
          sportCategory: 'basketball',
          leagueCode: 'NBA',
          leagueName: 'NBA',
          gender: 'men',
          teamName: 'San Antonio Spurs',
          teamAbbrev: 'SAS',
          teamId: 'spurs',
          focusTeams: ['San Antonio Spurs', 'Oklahoma City Thunder'],
          contextLevel: 'game',
          selectedGameId: activeGameId,
          model: 'gemini-3-pro-preview',
          gameAnalysisContext: context,
        });
      } else {
        // LEAGUE MODE: SSE streaming with reasoning trace
        data = await sendStreamingMessage(userMessage, {
          message: userMessage,
          history: messages.slice(-6).map(m => ({ role: m.role, content: m.content })),
          sport: 'NBA',
          sportCategory: 'basketball',
          leagueCode: 'NBA',
          leagueName: 'NBA',
          gender: 'men',
          teamName: 'San Antonio Spurs',
          teamAbbrev: 'SAS',
          teamId: 'spurs',
          focusTeams: ['San Antonio Spurs', 'Oklahoma City Thunder'],
          contextLevel,
        });
      }

      if (data) {
        setMessages(prev => [...prev, {
          role: 'assistant',
          content: data.content || data.message || data.response || 'I couldn\'t process that request.',
          source: data.source || contextLevel,
          analysis_id: data.analysis_id,
          reasoningSteps: [...reasoningSteps], // Snapshot for message history
        }]);
      }
    } catch (err) {
      console.error('Chat error:', err);
      setMessages(prev => [...prev, {
        role: 'assistant',
        content: 'Sorry, I encountered an error. Please try again.'
      }]);
    } finally {
      setIsLoading(false);
    }
  };

  const contextLevels = [
    { id: 'segment', label: 'Segment', icon: '📍', desc: `Seg ${currentSegment + 1}` },
    { id: 'game', label: 'Game', icon: '🏀', desc: isAutoLoadingSegments ? `Loading ${loadedSegments}/${totalSegments}` : `${loadedSegments} segs` },
    { id: 'league', label: 'League', icon: '🌐', desc: 'NBA Data' },
  ];

  return (
    <div className="h-full flex flex-col bg-gradient-to-b from-transparent to-zinc-950/50">
      {/* Context Level Selector */}
      <div data-tour-anchor="context-levels" className="flex-shrink-0 px-3 py-2 border-b border-white/[0.06] bg-zinc-900/30">
        <div className="flex gap-1.5">
          {contextLevels.map(level => (
            <button
              key={level.id}
              onClick={() => !level.disabled && setContextLevel(level.id)}
              disabled={level.disabled}
              className={`flex-1 px-2.5 py-1.5 rounded-lg text-[11px] font-medium transition-all ${
                contextLevel === level.id
                  ? 'bg-amber-500/20 border border-amber-500/40 text-amber-400'
                  : level.disabled
                    ? 'bg-zinc-800/30 text-white/20 cursor-not-allowed'
                    : 'bg-zinc-800/50 border border-white/[0.06] text-white/50 hover:text-white/70 hover:bg-zinc-700/50'
              }`}
            >
              <span className="mr-1">{level.icon}</span>
              {level.label}
            </button>
          ))}
        </div>
      </div>

      {/* Messages Area */}
      <div className="flex-1 overflow-y-auto px-3 py-2 space-y-2 custom-scrollbar min-h-0">
        {messages.length === 0 ? (
          // Empty State - shows current context
          <div className="h-full flex flex-col items-center justify-center text-center py-3">
            <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-cyan-500/20 to-blue-500/20 flex items-center justify-center mb-2">
              <Brain size={20} className="text-cyan-400" />
            </div>
            <p className="text-xs text-white/60 font-medium mb-1">
              {contextLevel === 'segment' ? `${gameContext?.name} - Segment ${currentSegment + 1}` : gameContext?.name}
            </p>
            <p className="text-xs text-white/30 max-w-[220px]">
              {contextLevel === 'segment'
                ? "Ask anything — Gemini will re-watch the video to answer"
                : contextLevel === 'game'
                  ? isAutoLoadingSegments ? `Loading game data (${loadedSegments}/${totalSegments})...` : `Full game analysis — ${loadedSegments} segments + BigQuery NL2SQL`
                  : "Ask anything — NL2SQL, box scores, shot charts, team stats"}
            </p>
            {contextLevel === 'segment' && priorSegmentAnalyses.length > 0 && (
              <p className="text-[10px] text-emerald-400/40 mt-1">
                {priorSegmentAnalyses.length} prior {priorSegmentAnalyses.length === 1 ? 'analysis' : 'analyses'} for this segment
              </p>
            )}
          </div>
        ) : (
          messages.map((msg, i) => (
            <div key={i} className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}>
              <div className={`max-w-[85%] ${
                msg.role === 'user'
                  ? 'bg-gradient-to-r from-amber-500/25 to-orange-500/20 border border-amber-500/30 rounded-2xl rounded-br-md'
                  : 'bg-zinc-800/80 border border-white/[0.08] rounded-2xl rounded-bl-md'
              } px-3 py-2 shadow-lg`}>
                {msg.role === 'assistant' && (
                  <div className="flex items-center gap-1.5 mb-1.5 pb-1.5 border-b border-white/[0.06]">
                    <div className={`w-4 h-4 rounded-full ${msg.source === 'segment-reanalysis' ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gradient-to-r from-cyan-500 to-blue-500'} flex items-center justify-center`}>
                      {msg.source === 'segment-reanalysis' ? <Video size={10} className="text-white" /> : <Zap size={10} className="text-white" />}
                    </div>
                    <span className={`text-xs font-medium uppercase tracking-wider ${msg.source === 'segment-reanalysis' ? 'text-emerald-400/80' : 'text-cyan-400/80'}`}>
                      {msg.source === 'segment-reanalysis' ? 'Video Re-Analysis' : 'Analysis'}
                    </span>
                  </div>
                )}
                {msg.role === 'assistant' ? (
                  <>
                    <MarkdownMessage content={msg.content} onNavigateSegment={onNavigateSegment} />
                    {msg.content?.includes('<!--HEATMAP_DATA:') && (
                      <HeatmapFromContent content={msg.content} />
                    )}
                  </>
                ) : (
                  <p className="text-xs leading-relaxed text-white/90">{msg.content}</p>
                )}
              </div>
            </div>
          ))
        )}
        {isLoading && (
          <div className="flex justify-start">
            <div className={`max-w-[90%] ${isReanalyzing ? 'bg-emerald-900/30 border-emerald-500/20' : 'bg-zinc-800/80 border-white/[0.08]'} border rounded-2xl rounded-bl-md px-3 py-2`}>
              {/* Reasoning trace for game/league streaming */}
              {reasoningSteps.length > 0 && !isReanalyzing ? (
                <ReasoningTrace steps={reasoningSteps} isActive={isLoading} />
              ) : (
                <>
                  <div className="flex items-center gap-2">
                    <div className={`w-4 h-4 rounded-full ${isReanalyzing ? 'bg-gradient-to-r from-emerald-500 to-teal-500' : 'bg-gradient-to-r from-cyan-500 to-blue-500'} flex items-center justify-center`}>
                      <Loader2 size={10} className="text-white animate-spin" />
                    </div>
                    <span className={`text-xs ${isReanalyzing ? 'text-emerald-400/70' : 'text-white/50'}`}>
                      {isReanalyzing ? 'Re-analyzing video segment...' : 'Connecting to TRON...'}
                    </span>
                  </div>
                  {isReanalyzing && (
                    <p className="text-[10px] text-emerald-400/40 mt-1 ml-6">Gemini 3 Flash is watching the video</p>
                  )}
                </>
              )}
            </div>
          </div>
        )}
        <div ref={messagesEndRef} />
      </div>

      {/* Input Area */}
      <div className="flex-shrink-0 p-2 border-t border-white/[0.06] bg-zinc-900/50">
        {/* Input Field */}
        <div className="flex gap-2">
          <input
            type="text"
            value={input}
            onChange={(e) => setInput(e.target.value)}
            onKeyDown={(e) => e.key === 'Enter' && !e.shiftKey && sendMessage()}
            placeholder={contextLevel === 'segment' ? "Ask anything — AI re-watches the video..." : contextLevel === 'game' ? "Ask about the full game..." : "Ask about the league..."}
            className="flex-1 bg-zinc-800/60 border border-white/[0.08] rounded-xl px-3.5 py-2.5 text-sm focus:outline-none focus:border-cyan-500/40 focus:bg-zinc-800/80 placeholder:text-white/30 transition-all"
          />
          <button
            onClick={sendMessage}
            disabled={!input.trim() || isLoading}
            className="px-3 py-2.5 bg-gradient-to-r from-cyan-500 to-blue-500 hover:from-cyan-400 hover:to-blue-400 disabled:from-zinc-700 disabled:to-zinc-700 disabled:text-white/30 rounded-xl transition-all shadow-lg shadow-cyan-500/20 disabled:shadow-none"
          >
            <Send size={16} />
          </button>
        </div>
      </div>
    </div>
  );
};

const GuidedTourOverlay = ({ active, steps, stepIndex, onClose, onPrev, onNext }) => {
  const [targetRect, setTargetRect] = useState(null);

  useEffect(() => {
    if (!active) return;

    const updateTarget = () => {
      const step = steps[stepIndex];
      if (!step?.target) {
        setTargetRect(null);
        return;
      }

      const target = document.querySelector(`[data-tour-anchor="${step.target}"]`);
      if (!target) {
        setTargetRect(null);
        return;
      }

      const rect = target.getBoundingClientRect();
      setTargetRect(rect);
    };

    updateTarget();
    window.addEventListener('resize', updateTarget);
    window.addEventListener('scroll', updateTarget, true);

    return () => {
      window.removeEventListener('resize', updateTarget);
      window.removeEventListener('scroll', updateTarget, true);
    };
  }, [active, steps, stepIndex]);

  if (!active) return null;

  const step = steps[stepIndex];
  if (!step) return null;

  const vw = typeof window !== 'undefined' ? window.innerWidth : 1440;
  const vh = typeof window !== 'undefined' ? window.innerHeight : 900;
  const margin = 16;
  const tooltipWidth = Math.min(360, vw - margin * 2);
  const estimatedHeight = 260;

  let tooltipLeft = margin;
  let tooltipTop = Math.max(margin, (vh - estimatedHeight) / 2);

  if (targetRect) {
    const targetCenterY = targetRect.top + targetRect.height / 2;
    const canPlaceRight = targetRect.right + 18 + tooltipWidth <= vw - margin;
    const canPlaceLeft = targetRect.left - 18 - tooltipWidth >= margin;

    if (canPlaceRight) {
      tooltipLeft = targetRect.right + 18;
    } else if (canPlaceLeft) {
      tooltipLeft = targetRect.left - tooltipWidth - 18;
    } else {
      tooltipLeft = Math.min(vw - tooltipWidth - margin, Math.max(margin, targetRect.left));
    }

    tooltipTop = Math.min(vh - estimatedHeight - margin, Math.max(margin, targetCenterY - estimatedHeight / 2));
  }

  const isLastStep = stepIndex === steps.length - 1;
  const highlightPad = 6;

  return (
    <>
      <div className="fixed inset-0 z-[140] bg-black/80 backdrop-blur-[2px]" onClick={onClose} />

      {targetRect && (
        <div
          className="fixed z-[141] rounded-xl border-2 border-amber-400/80 shadow-[0_0_0_1px_rgba(251,191,36,0.4),0_0_28px_rgba(251,191,36,0.32)] pointer-events-none transition-all duration-200"
          style={{
            top: targetRect.top - highlightPad,
            left: targetRect.left - highlightPad,
            width: targetRect.width + highlightPad * 2,
            height: targetRect.height + highlightPad * 2,
          }}
        />
      )}

      <div
        className="fixed z-[145] rounded-2xl border border-white/20 bg-zinc-950/95 backdrop-blur-xl shadow-[0_20px_48px_rgba(2,6,23,0.6)] p-4 text-white"
        style={{ width: tooltipWidth, left: tooltipLeft, top: tooltipTop }}
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-start justify-between gap-3">
          <div>
            <p className="text-[10px] uppercase tracking-[0.14em] text-amber-300/85 font-semibold">Widget Guide</p>
            <h3 className="text-sm font-semibold text-white/95 mt-1 leading-snug">{step.title}</h3>
          </div>
          <button
            onClick={onClose}
            className="p-1 rounded-md text-white/45 hover:text-white/85 hover:bg-white/10 transition-colors"
            title="Close guide"
          >
            <X size={14} />
          </button>
        </div>

        <p className="text-xs text-white/75 leading-relaxed mt-2">{step.description}</p>
        <p className="text-xs text-cyan-300/85 leading-relaxed mt-2"><span className="font-semibold text-cyan-200">What to look at:</span> {step.lookFor}</p>

        <div className="mt-3 flex items-center justify-between">
          <div className="flex items-center gap-1.5">
            {steps.map((_, idx) => (
              <span
                key={idx}
                className={`h-1.5 rounded-full transition-all ${idx === stepIndex ? 'w-4 bg-amber-400' : 'w-1.5 bg-white/30'}`}
              />
            ))}
          </div>
          <span className="text-[11px] text-white/50 font-mono">{stepIndex + 1}/{steps.length}</span>
        </div>

        <div className="mt-3 flex items-center justify-between">
          <button
            onClick={onPrev}
            disabled={stepIndex === 0}
            className="px-3 py-1.5 rounded-lg border border-white/15 text-xs font-medium text-white/75 hover:bg-white/10 disabled:opacity-35 disabled:cursor-not-allowed transition-colors"
          >
            Back
          </button>
          <button
            onClick={isLastStep ? onClose : onNext}
            className="px-3 py-1.5 rounded-lg bg-gradient-to-r from-amber-500 to-orange-500 text-xs font-semibold text-slate-950 hover:from-amber-400 hover:to-orange-400 transition-colors"
          >
            {isLastStep ? 'Done' : 'Next'}
          </button>
        </div>
      </div>
    </>
  );
};

// ============================================================================
// MAIN DASHBOARD
// ============================================================================

const SpursOKCDashboard = () => {
  // Team context from sport selector (NatStat uses team_name for city, full_name for full)
  const { selectedTeam } = useTeam();
  const leagueTeamName = selectedTeam?.team?.full_name || selectedTeam?.team?.team_name || selectedTeam?.teamName || 'San Antonio Spurs';
  const leagueTeamAbbrev = selectedTeam?.team?.team_code || selectedTeam?.teamAbbrev || 'SAS';
  const leagueTeamId = selectedTeam?.team?.team_id || selectedTeam?.teamId || 'spurs';

  // State — selectedGame and currentSegment persist to localStorage
  const [selectedGame, setSelectedGame] = useState(() => {
    try {
      const savedId = localStorage.getItem('slam_spursokc_game');
      if (savedId) {
        const found = GAMES.find(g => g.id === JSON.parse(savedId));
        if (found) return found;
      }
    } catch { /* ignore */ }
    return GAMES[0];
  });
  const [currentTime, setCurrentTime] = useState(0);
  const [currentSegment, setCurrentSegment] = usePersistedState('slam_spursokc_segment', 0);
  const [analysis, setAnalysis] = useState(null);
  const [analysisCache, setAnalysisCache] = useState({});
  const analysisCacheRef = useRef(analysisCache);
  useEffect(() => { analysisCacheRef.current = analysisCache; }, [analysisCache]);
  const [isPlaying, setIsPlaying] = useState(false);
  const [isMuted, setIsMuted] = useState(false); // Audio enabled by default for game analysis
  const [playbackSpeed, setPlaybackSpeed] = useState(1);
  const [isLoading, setIsLoading] = useState(false);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [showGameSelector, setShowGameSelector] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const [videoUrl, setVideoUrl] = useState(null);
  const [videoLoading, setVideoLoading] = useState(true);
  const [autoPlayOnLoad, setAutoPlayOnLoad] = useState(false);
  const [isAutoLoadingSegments, setIsAutoLoadingSegments] = useState(false);
  const [featuredId, setFeaturedId] = useState(null);
  const [isTourActive, setIsTourActive] = useState(false);
  const [tourStepIndex, setTourStepIndex] = useState(0);

  // Grid resize state (crosshair drag handles)
  const [rowSplit, setRowSplit] = useState(60); // top row height %
  const [colSplit, setColSplit] = useState(55); // left column width %
  const gridRef = useRef(null);
  const isDraggingAxis = useRef(null); // 'row' | 'col' | 'both' | null

  const startGridDrag = useCallback((axis) => (e) => {
    e.preventDefault();
    isDraggingAxis.current = axis;
    document.body.style.userSelect = 'none';
    document.body.style.cursor = axis === 'row' ? 'row-resize' : axis === 'col' ? 'col-resize' : 'move';
  }, []);

  const toggleTour = useCallback(() => {
    setIsTourActive((prev) => {
      if (prev) return false;
      setTourStepIndex(0);
      return true;
    });
  }, []);

  const closeTour = useCallback(() => {
    setIsTourActive(false);
  }, []);

  const nextTourStep = useCallback(() => {
    setTourStepIndex((prev) => Math.min(prev + 1, TOUR_STEPS.length - 1));
  }, []);

  const prevTourStep = useCallback(() => {
    setTourStepIndex((prev) => Math.max(prev - 1, 0));
  }, []);

  useEffect(() => {
    const onMove = (e) => {
      if (!isDraggingAxis.current || !gridRef.current) return;
      const rect = gridRef.current.getBoundingClientRect();
      if (isDraggingAxis.current === 'row' || isDraggingAxis.current === 'both') {
        const pct = ((e.clientY - rect.top) / rect.height) * 100;
        setRowSplit(Math.max(20, Math.min(80, pct)));
      }
      if (isDraggingAxis.current === 'col' || isDraggingAxis.current === 'both') {
        const pct = ((e.clientX - rect.left) / rect.width) * 100;
        setColSplit(Math.max(25, Math.min(75, pct)));
      }
    };
    const onUp = () => {
      isDraggingAxis.current = null;
      document.body.style.userSelect = '';
      document.body.style.cursor = '';
    };
    document.addEventListener('mousemove', onMove);
    document.addEventListener('mouseup', onUp);
    return () => {
      document.removeEventListener('mousemove', onMove);
      document.removeEventListener('mouseup', onUp);
    };
  }, []);

  // Refs
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const activeGameIdRef = useRef(selectedGame.id);
  const videoRequestKeyRef = useRef(null);

  useEffect(() => {
    activeGameIdRef.current = selectedGame.id;
  }, [selectedGame.id]);

  // Persist selected game ID to localStorage
  useEffect(() => {
    try {
      localStorage.setItem('slam_spursokc_game', JSON.stringify(selectedGame.id));
    } catch { /* localStorage full or unavailable */ }
  }, [selectedGame.id]);

  // NOTE: Each segment is a separate video file (seg_0000.mp4, seg_0001.mp4, etc.)
  // currentTime is the time within the current segment video (0 to ~60s), NOT global game time
  // Segment changes happen via: user nav buttons, timeline clicks, or video end (auto-advance)

  // Load analysis for current segment (v3 first, fallback v2; local first, fallback GCS)
  // Uses analysisCacheRef to avoid re-creating callback on every cache update
  const loadAnalysis = useCallback(async (segIndex) => {
    const requestGameId = selectedGame.id;
    const requestGameFolder = selectedGame.folder;

    const isCurrentSegment = () =>
      activeGameIdRef.current === requestGameId && currentSegmentRef.current === segIndex;

    // Check cache via ref to avoid dependency cycle
    if (analysisCacheRef.current[segIndex]) {
      if (isCurrentSegment()) {
        setAnalysis(analysisCacheRef.current[segIndex]);
      }
      return;
    }

    if (isCurrentSegment()) setIsLoading(true);
    try {
      const segId = `seg${segIndex.toString().padStart(4, '0')}`;
      const folderEncoded = requestGameFolder.replace(/ /g, '_');

      const gcsUrlV3 = gcsToProxyUrl(`analysis/v3/${folderEncoded}/${segId}.json`);
      const gcsUrlV2 = gcsToProxyUrl(`analysis/${GAME_SET_ID}/${folderEncoded}/${segId}.json`);
      const isLocal = window.location.hostname === 'localhost';

      let response;
      if (isLocal) {
        response = await fetch(`/api/local-analysis-v3/${requestGameId}/${segId}.json`);
        if (!response.ok) response = await fetch(`/api/local-analysis/${requestGameId}/${segId}.json`);
        if (!response.ok) response = await fetch(gcsUrlV3);
        if (!response.ok) response = await fetch(gcsUrlV2);
      } else {
        // Production: try v3 first, fall back to v2
        response = await fetch(gcsUrlV3);
        if (!response.ok) response = await fetch(gcsUrlV2);
      }

      if (response.ok) {
        const data = await response.json();
        if (activeGameIdRef.current !== requestGameId) return;
        setAnalysisCache(prev => ({ ...prev, [segIndex]: data }));
        if (isCurrentSegment()) setAnalysis(data);
      } else {
        if (isCurrentSegment()) setAnalysis(null);
      }
    } catch (err) {
      console.error('Error loading analysis:', err);
      if (isCurrentSegment()) setAnalysis(null);
    } finally {
      if (isCurrentSegment()) setIsLoading(false);
    }
  }, [selectedGame.id, selectedGame.folder]);

  // Load analysis when segment changes — set from cache synchronously
  useEffect(() => {
    currentSegmentRef.current = currentSegment;
    if (analysisCache[currentSegment]) {
      setAnalysis(analysisCache[currentSegment]);
    } else {
      loadAnalysis(currentSegment);
    }
  }, [currentSegment, loadAnalysis]); // eslint-disable-line react-hooks/exhaustive-deps

  // Preload adjacent segments (only on segment change, not on cache updates)
  useEffect(() => {
    const preload = [currentSegment - 1, currentSegment + 1, currentSegment + 2];
    preload.forEach(seg => {
      if (seg >= 0 && seg < selectedGame.segments && !analysisCacheRef.current[seg]) {
        loadAnalysis(seg);
      }
    });
  }, [currentSegment, selectedGame.segments, loadAnalysis]);

  // Load all segments for game-level context (batch load)
  const loadAllSegments = useCallback(async () => {
    const requestGameId = selectedGame.id;
    const requestGameFolder = selectedGame.folder;
    const cache = analysisCacheRef.current;
    const missingSegments = [];
    for (let i = 0; i < selectedGame.segments; i++) {
      if (!cache[i]) {
        missingSegments.push(i);
      }
    }

    if (missingSegments.length === 0) return;

    console.log(`Loading ${missingSegments.length} remaining segments for game context...`);

    // Load in batches of 10 to avoid overwhelming the server
    const BATCH_SIZE = 10;
    for (let batch = 0; batch < missingSegments.length; batch += BATCH_SIZE) {
      if (activeGameIdRef.current !== requestGameId) {
        return;
      }
      const batchSegments = missingSegments.slice(batch, batch + BATCH_SIZE);

      await Promise.all(batchSegments.map(async (segIndex) => {
        const segId = `seg${segIndex.toString().padStart(4, '0')}`;
        const folderEncoded = requestGameFolder.replace(/ /g, '_');
        try {
          const gcsUrlV3 = gcsToProxyUrl(`analysis/v3/${folderEncoded}/${segId}.json`);
          const gcsUrlV2 = gcsToProxyUrl(`analysis/${GAME_SET_ID}/${folderEncoded}/${segId}.json`);
          const isLocal = window.location.hostname === 'localhost';

          let response;
          if (isLocal) {
            response = await fetch(`/api/local-analysis-v3/${requestGameId}/${segId}.json`);
            if (!response.ok) response = await fetch(`/api/local-analysis/${requestGameId}/${segId}.json`);
            if (!response.ok) response = await fetch(gcsUrlV3);
            if (!response.ok) response = await fetch(gcsUrlV2);
          } else {
            response = await fetch(gcsUrlV3);
            if (!response.ok) response = await fetch(gcsUrlV2);
          }

          if (response.ok) {
            const data = await response.json();
            if (activeGameIdRef.current === requestGameId) {
              setAnalysisCache(prev => ({ ...prev, [segIndex]: data }));
            }
          }
        } catch (err) {
          console.error(`Error loading segment ${segIndex}:`, err);
        }
      }));

      // Small delay between batches
      if (batch + BATCH_SIZE < missingSegments.length) {
        await new Promise(resolve => setTimeout(resolve, 100));
      }
    }

    console.log('All segments loaded for game context');
  }, [selectedGame.id, selectedGame.folder, selectedGame.segments]);

  // Track current segment for race-condition guard in loadAnalysis
  const currentSegmentRef = useRef(currentSegment);
  useEffect(() => { currentSegmentRef.current = currentSegment; }, [currentSegment]);

  // Track if this is a user-initiated segment change (for auto-play)
  const prevSegmentRef = useRef(currentSegment);

  // Load signed URL for video segment
  useEffect(() => {
    let cancelled = false;
    const requestKey = `${selectedGame.id}:${currentSegment}`;
    videoRequestKeyRef.current = requestKey;

    const loadVideoUrl = async () => {
      // Determine if this is a segment navigation (not initial load)
      const isSegmentChange = prevSegmentRef.current !== currentSegment;
      prevSegmentRef.current = currentSegment;

      setVideoLoading(true);
      setVideoError(false);
      // Auto-play if user navigated to a new segment
      if (isSegmentChange) {
        setAutoPlayOnLoad(true);
      }

      const gcsPath = `segments/${GAME_SET_ID}/${selectedGame.folder}/seg_${currentSegment.toString().padStart(4, '0')}.mp4`;

      try {
        const response = await fetch(`/api/gcs-signed-url?path=${encodeURIComponent(gcsPath)}&bucket=${GCS_BUCKET}`);
        if (cancelled || videoRequestKeyRef.current !== requestKey) return;

        if (response.ok) {
          const data = await response.json();
          setVideoUrl(data.data?.url || data.url);
          setVideoError(false);
        } else {
          console.log('Video signed URL failed:', response.status);
          setVideoError(true);
          setVideoUrl(null);
        }
      } catch (err) {
        if (cancelled || videoRequestKeyRef.current !== requestKey) return;
        console.error('Error getting video signed URL:', err);
        setVideoError(true);
        setVideoUrl(null);
      } finally {
        if (!cancelled && videoRequestKeyRef.current === requestKey) {
          setVideoLoading(false);
        }
      }
    };

    loadVideoUrl();
    return () => {
      cancelled = true;
    };
  }, [currentSegment, selectedGame.id, selectedGame.folder]);

  // Reset on game change
  useEffect(() => {
    setCurrentTime(0);
    setCurrentSegment(0);
    setAnalysis(null);
    setAnalysisCache({});
    setVideoError(false);
    setVideoUrl(null);
    setVideoLoading(true);
    setAutoPlayOnLoad(false);
    prevSegmentRef.current = 0;
    if (videoRef.current) {
      videoRef.current.currentTime = 0;
    }
  }, [selectedGame.id]);

  // Auto-load all segments for game context after game change
  useEffect(() => {
    let cancelled = false;
    const autoLoad = async () => {
      // Small delay to let the current segment load first
      await new Promise(r => setTimeout(r, 500));
      if (cancelled) return;
      setIsAutoLoadingSegments(true);
      try {
        await loadAllSegments();
      } finally {
        if (!cancelled) setIsAutoLoadingSegments(false);
      }
    };
    autoLoad();
    return () => { cancelled = true; };
  }, [selectedGame.id, loadAllSegments]);

  // Video time update
  const handleTimeUpdate = () => {
    if (videoRef.current) {
      setCurrentTime(videoRef.current.currentTime);
    }
  };

  // Seek to time
  const handleSeek = (time) => {
    if (videoRef.current) {
      videoRef.current.currentTime = time;
      setCurrentTime(time);
    }
  };

  // Playback controls
  const togglePlay = () => {
    if (videoRef.current) {
      if (isPlaying) videoRef.current.pause();
      else videoRef.current.play();
      setIsPlaying(!isPlaying);
    }
  };

  // Frame-by-frame stepping (~1/30s per frame)
  const FRAME_DURATION = 1 / 30;
  const stepFrame = (direction) => {
    if (videoRef.current) {
      videoRef.current.pause();
      setIsPlaying(false);
      const newTime = Math.max(0, Math.min(videoRef.current.duration, videoRef.current.currentTime + (direction * FRAME_DURATION)));
      videoRef.current.currentTime = newTime;
      setCurrentTime(newTime);
    }
  };

  // Playback speed control
  const SPEED_OPTIONS = [0.25, 0.5, 1, 1.5, 2];
  const cycleSpeed = () => {
    const idx = SPEED_OPTIONS.indexOf(playbackSpeed);
    const next = SPEED_OPTIONS[(idx + 1) % SPEED_OPTIONS.length];
    setPlaybackSpeed(next);
    if (videoRef.current) videoRef.current.playbackRate = next;
  };

  // Sync playback speed when video loads
  useEffect(() => {
    if (videoRef.current) videoRef.current.playbackRate = playbackSpeed;
  }, [videoUrl, playbackSpeed]);

  // Fullscreen
  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      containerRef.current?.requestFullscreen();
      setIsFullscreen(true);
    } else {
      document.exitFullscreen();
      setIsFullscreen(false);
    }
  };

  const totalSegmentsAcrossGames = useMemo(
    () => GAMES.reduce((total, game) => total + game.segments, 0),
    []
  );
  const loadedSegmentsForActiveGame = useMemo(
    () => Object.keys(analysisCache || {}).length,
    [analysisCache]
  );

  return (
    <FeaturedContext.Provider value={{ featuredId, setFeaturedId }}>
    {/* Force dark mode for this specialized game analysis dashboard */}
    <div className="dark">
    <div ref={containerRef} className="h-screen bg-black text-white font-['IBM_Plex_Sans','Segoe_UI',sans-serif] flex flex-col">
      <header className="flex-shrink-0 border-b border-white/[0.08] bg-black/80 backdrop-blur-xl z-50">
        <div className="max-w-[1760px] mx-auto px-3 md:px-5 py-2">
          <div className="flex flex-wrap items-center justify-between gap-2">
            <div className="flex items-center gap-2.5">
              <div className="w-8 h-8 rounded-xl bg-gradient-to-br from-amber-500 to-orange-600 flex items-center justify-center shadow-lg shadow-amber-500/25">
                <Zap size={15} />
              </div>
              <div>
                <h1 className="text-sm font-semibold tracking-tight">Spurs vs OKC Intelligence</h1>
                <p className="text-[11px] text-white/55">SLAM Sports AI</p>
              </div>
            </div>

            <div data-tour-anchor="game-selector" className="relative">
              <button
                onClick={() => setShowGameSelector(!showGameSelector)}
                className="min-w-[220px] flex items-center justify-between gap-2 px-2.5 py-1.5 bg-zinc-900/80 hover:bg-zinc-800/70 border border-white/[0.12] rounded-lg transition-colors text-xs"
              >
                <div className="text-left">
                  <div className="font-semibold text-white/90">{selectedGame.name}</div>
                  <div className="text-[11px] text-white/58">{selectedGame.date}</div>
                </div>
                <ChevronDown size={12} className={`text-white/40 transition-transform ${showGameSelector ? 'rotate-180' : ''}`} />
              </button>

              {showGameSelector && (
                <div className="absolute right-0 mt-2 w-64 bg-zinc-950/95 backdrop-blur-xl border border-white/[0.14] rounded-xl shadow-2xl overflow-hidden z-50">
                  {GAMES.map(game => (
                    <button
                      key={game.id}
                      onClick={() => { setSelectedGame(game); setShowGameSelector(false); }}
                      className={`w-full px-3 py-2 flex items-center justify-between hover:bg-white/[0.04] transition-colors text-xs ${
                        selectedGame.id === game.id ? 'bg-amber-500/10 border-l-2 border-amber-500' : ''
                      }`}
                    >
                      <div className="text-left">
                        <div className="font-semibold text-white/85">{game.name}</div>
                        <div className="text-[11px] text-white/40">{game.date}</div>
                      </div>
                      <div className="text-[11px] text-white/35">{game.segments} seg</div>
                    </button>
                  ))}
                </div>
              )}
            </div>

            <div data-tour-anchor="header-metrics" className="flex items-center gap-1.5">
              <div className="px-2 py-1.5 rounded-lg bg-zinc-900/80 border border-white/[0.12] text-[11px] text-white/75 flex items-center gap-1">
                <Layers size={11} />
                <span>{totalSegmentsAcrossGames}</span>
              </div>
              <div className="px-2 py-1.5 rounded-lg bg-zinc-900/80 border border-white/[0.12] text-[11px] text-white/75 flex items-center gap-1">
                <Grid3X3 size={11} />
                <span>{GAMES.length} Games</span>
              </div>
              <div className="px-2 py-1.5 rounded-lg bg-amber-500/15 border border-amber-500/30 text-[11px] text-amber-300 font-medium">
                Seg {currentSegment + 1}/{selectedGame.segments}
              </div>
            </div>
          </div>
        </div>
      </header>

      <button
        onClick={toggleTour}
        className={`fixed top-3 right-3 z-[160] w-8 h-8 rounded-full border transition-all flex items-center justify-center shadow-lg ${
          isTourActive
            ? 'bg-amber-500/90 border-amber-300 text-slate-950 shadow-amber-500/30'
            : 'bg-zinc-950/80 border-white/25 text-white/85 hover:bg-zinc-900/90 hover:border-white/40'
        }`}
        title={isTourActive ? 'Turn off guide' : 'Show widget guide'}
        aria-label={isTourActive ? 'Turn off guide' : 'Show widget guide'}
      >
        <HelpCircle size={14} />
      </button>

      <main className="flex-1 min-h-0 p-1">
        <div ref={gridRef} className="h-full relative overflow-hidden">

          {/* ═══ TL: Video Player ═══ */}
          <div data-tour-anchor="video-widget" className="absolute overflow-hidden" style={{
            top: 0, left: 0,
            width: `calc(${colSplit}% - 3px)`,
            height: `calc(${rowSplit}% - 3px)`,
          }}>
            <Widget
              id="video"
              title="Video"
              icon={Play}
              iconColor="bg-amber-500/20 text-amber-400"
              actions={
                <button
                  onClick={toggleFullscreen}
                  className="p-1.5 hover:bg-white/10 rounded-lg transition-colors"
                  title={isFullscreen ? 'Exit fullscreen' : 'Enter fullscreen'}
                >
                  {isFullscreen ? <Minimize2 size={13} /> : <Maximize2 size={13} />}
                </button>
              }
              className="h-full"
            >
              <div className="flex flex-col h-full min-h-0">
                <div className="flex-1 bg-black relative min-h-0">
                  {videoLoading ? (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950">
                      <Loader2 size={28} className="text-amber-500 animate-spin mb-2" />
                      <p className="text-[11px] text-white/40">Loading segment {currentSegment + 1}...</p>
                    </div>
                  ) : videoUrl && !videoError ? (
                    <video
                      key={`seg-${currentSegment}`}
                      ref={videoRef}
                      src={videoUrl}
                      className="w-full h-full object-contain"
                      muted={isMuted}
                      autoPlay={autoPlayOnLoad}
                      onTimeUpdate={handleTimeUpdate}
                      onPlay={() => { setIsPlaying(true); setAutoPlayOnLoad(false); }}
                      onPause={() => setIsPlaying(false)}
                      onCanPlay={() => {
                        if (autoPlayOnLoad && videoRef.current) {
                          videoRef.current.play().catch(err => console.log('Auto-play blocked:', err.message));
                        }
                      }}
                      onError={() => { console.log('Video load error'); setVideoError(true); }}
                      onEnded={() => {
                        if (currentSegment < selectedGame.segments - 1) setCurrentSegment(prev => prev + 1);
                      }}
                    />
                  ) : (
                    <div className="w-full h-full flex flex-col items-center justify-center bg-gradient-to-br from-zinc-900 via-zinc-900 to-zinc-950 relative">
                      <div className="absolute inset-0 opacity-5">
                        <div className="absolute inset-0" style={{ backgroundImage: 'radial-gradient(circle at 2px 2px, white 1px, transparent 0)', backgroundSize: '32px 32px' }} />
                      </div>
                      <div className="relative z-10 text-center">
                        <div className="w-10 h-10 mx-auto mb-2 rounded-xl bg-gradient-to-br from-amber-500/20 to-orange-500/20 border border-amber-500/30 flex items-center justify-center">
                          <Activity size={20} className="text-amber-500" />
                        </div>
                        <h3 className="text-xs font-semibold mb-1">Segment {currentSegment + 1}</h3>
                        <p className="text-[11px] text-white/40 mb-2">{selectedGame.folder}</p>
                        <div className="flex items-center justify-center gap-1.5">
                          <button onClick={() => setCurrentSegment(Math.max(0, currentSegment - 1))} disabled={currentSegment === 0} className="p-1.5 bg-zinc-800/60 hover:bg-zinc-700/60 disabled:opacity-30 disabled:cursor-not-allowed border border-white/[0.08] rounded-lg transition-all"><SkipBack size={12} /></button>
                          <div className="px-2 py-1 bg-amber-500/10 border border-amber-500/30 rounded-lg"><span className="text-amber-400 font-mono text-[11px]">{currentSegment + 1}/{selectedGame.segments}</span></div>
                          <button onClick={() => setCurrentSegment(Math.min(selectedGame.segments - 1, currentSegment + 1))} disabled={currentSegment >= selectedGame.segments - 1} className="p-1.5 bg-zinc-800/60 hover:bg-zinc-700/60 disabled:opacity-30 disabled:cursor-not-allowed border border-white/[0.08] rounded-lg transition-all"><SkipForward size={12} /></button>
                        </div>
                      </div>
                    </div>
                  )}

                  {!isPlaying && !videoError && !videoLoading && videoUrl && (
                    <button onClick={togglePlay} className="absolute inset-0 flex items-center justify-center bg-black/20 hover:bg-black/30 transition-colors">
                      <div className="w-14 h-14 rounded-full bg-white/10 backdrop-blur-sm flex items-center justify-center hover:bg-white/20 transition-colors">
                        <Play size={24} fill="white" className="text-white ml-1" />
                      </div>
                    </button>
                  )}

                  {!videoError && !videoLoading && videoUrl && (
                    <div className="absolute top-1.5 left-1.5 px-1.5 py-0.5 bg-zinc-950/80 backdrop-blur-sm rounded text-[10px] font-mono border border-white/[0.12]">
                      Seg {currentSegment + 1}/{selectedGame.segments}
                    </div>
                  )}
                </div>

                {/* Compact controls bar with frame-by-frame */}
                <div className="flex-shrink-0 px-1 py-0.5 border-t border-white/[0.06] flex items-center gap-0.5">
                  <button onClick={() => setCurrentSegment(Math.max(0, currentSegment - 1))} disabled={currentSegment === 0 || videoLoading} className="p-1 hover:bg-white/10 disabled:opacity-30 rounded transition-colors" title="Previous segment"><SkipBack size={11} /></button>
                  <button onClick={() => stepFrame(-1)} disabled={videoError || videoLoading || !videoUrl} className="p-1 hover:bg-white/10 disabled:opacity-30 rounded transition-colors" title="Step back 1 frame"><ChevronLeft size={11} /></button>
                  <button onClick={togglePlay} disabled={videoError || videoLoading || !videoUrl} className="p-1 hover:bg-white/10 disabled:opacity-30 rounded transition-colors" title={isPlaying ? 'Pause' : 'Play'}>{isPlaying ? <Pause size={11} /> : <Play size={11} />}</button>
                  <button onClick={() => stepFrame(1)} disabled={videoError || videoLoading || !videoUrl} className="p-1 hover:bg-white/10 disabled:opacity-30 rounded transition-colors" title="Step forward 1 frame"><ChevronRight size={11} /></button>
                  <button onClick={() => setCurrentSegment(Math.min(selectedGame.segments - 1, currentSegment + 1))} disabled={currentSegment >= selectedGame.segments - 1 || videoLoading} className="p-1 hover:bg-white/10 disabled:opacity-30 rounded transition-colors" title="Next segment"><SkipForward size={11} /></button>
                  <div className="w-px h-3 bg-white/10 mx-0.5" />
                  <button onClick={cycleSpeed} disabled={videoError || videoLoading || !videoUrl} className="px-1.5 py-0.5 hover:bg-white/10 disabled:opacity-30 rounded transition-colors text-[10px] font-mono text-amber-400/80" title="Playback speed">{playbackSpeed}x</button>
                  <button onClick={() => setIsMuted(!isMuted)} disabled={videoError || videoLoading || !videoUrl} className="p-1 hover:bg-white/10 disabled:opacity-30 rounded transition-colors" title={isMuted ? 'Unmute' : 'Mute'}>{isMuted ? <VolumeX size={11} /> : <Volume2 size={11} />}</button>
                  <div className="flex-1" />
                  {videoLoading && <Loader2 size={9} className="text-amber-500 animate-spin mr-0.5" />}
                  <span className="text-[10px] font-mono text-white/40">{loadedSegmentsForActiveGame} loaded</span>
                </div>

                <div data-tour-anchor="timeline-widget">
                  <TimelineScrubber
                    currentTime={currentTime}
                    duration={selectedGame.duration}
                    segments={selectedGame.segments}
                    analysisCache={analysisCache}
                    onSeek={handleSeek}
                    onSegmentChange={setCurrentSegment}
                    currentSegment={currentSegment}
                  />
                </div>
              </div>
            </Widget>
          </div>

          {/* ═══ TR: Game Intelligence Chat ═══ */}
          <div data-tour-anchor="chat-widget" className="absolute overflow-hidden" style={{
            top: 0,
            left: `calc(${colSplit}% + 3px)`,
            right: 0,
            height: `calc(${rowSplit}% - 3px)`,
          }}>
            <Widget
              id="chat"
              title="Game Intelligence Chat"
              icon={MessageSquare}
              iconColor="bg-gradient-to-r from-blue-500/20 to-cyan-500/20 text-cyan-400"
              className="h-full"
            >
              <ChatInterface
                analysis={analysis}
                gameContext={selectedGame}
                currentSegment={currentSegment}
                allSegmentAnalyses={analysisCache}
                isAutoLoadingSegments={isAutoLoadingSegments}
                onNavigateSegment={setCurrentSegment}
              />
            </Widget>
          </div>

          {/* ═══ BL: Segment Analysis ═══ */}
          <div data-tour-anchor="analysis-widget" className="absolute overflow-hidden" style={{
            top: `calc(${rowSplit}% + 3px)`,
            left: 0,
            width: `calc(${colSplit}% - 3px)`,
            bottom: 0,
          }}>
            <Widget
              id="analysis"
              title="Segment Analysis"
              icon={Brain}
              iconColor="bg-purple-500/20 text-purple-400"
              className="h-full"
            >
              <AnalysisPanel
                analysis={analysis}
                isLoading={isLoading}
              />
            </Widget>
          </div>

          {/* ═══ BR: Visualizations ═══ */}
          <div data-tour-anchor="viz-widget" className="absolute overflow-hidden" style={{
            top: `calc(${rowSplit}% + 3px)`,
            left: `calc(${colSplit}% + 3px)`,
            right: 0,
            bottom: 0,
          }}>
            <Widget
              id="drive"
              title={`Visualizations — Seg ${currentSegment + 1}`}
              icon={Target}
              iconColor="bg-emerald-500/20 text-emerald-400"
              className="h-full"
            >
              <div className="p-1.5 overflow-auto h-full custom-scrollbar">
                <SegmentSnapshot analysis={analysis} segmentIndex={currentSegment} />
                {analysis?.mcpResource ? (<MCPAppRenderer resourceUri={analysis.mcpResource.uri} toolName={analysis.mcpResource.tool} toolResult={analysis.mcpResource.data} />) : (<div className="flex items-center justify-center h-full min-h-[200px] text-white/30 text-xs">No active visualization</div>)}
              </div>
            </Widget>
          </div>

          {/* ═══ Horizontal resize handle (row split) ═══ */}
          <div
            className="absolute left-0 right-0 z-10 flex items-center justify-center group cursor-row-resize"
            style={{ top: `calc(${rowSplit}% - 3px)`, height: '6px' }}
            onMouseDown={startGridDrag('row')}
          >
            <div className="h-[2px] w-16 rounded-full bg-white/[0.08] group-hover:bg-amber-500/60 group-active:bg-amber-500 transition-colors" />
          </div>

          {/* ═══ Vertical resize handle (column split) ═══ */}
          <div
            className="absolute top-0 bottom-0 z-10 flex items-center justify-center group cursor-col-resize"
            style={{ left: `calc(${colSplit}% - 3px)`, width: '6px' }}
            onMouseDown={startGridDrag('col')}
          >
            <div className="w-[2px] h-16 rounded-full bg-white/[0.08] group-hover:bg-amber-500/60 group-active:bg-amber-500 transition-colors" />
          </div>

          {/* ═══ Center intersection (drag both axes) ═══ */}
          <div
            className="absolute z-20 cursor-move flex items-center justify-center group"
            style={{
              top: `calc(${rowSplit}% - 5px)`,
              left: `calc(${colSplit}% - 5px)`,
              width: '10px',
              height: '10px',
            }}
            onMouseDown={startGridDrag('both')}
          >
            <div className="w-2 h-2 rounded-full bg-white/10 group-hover:bg-amber-500/50 group-active:bg-amber-500 transition-colors" />
          </div>

        </div>
      </main>

      <GuidedTourOverlay
        active={isTourActive}
        steps={TOUR_STEPS}
        stepIndex={tourStepIndex}
        onClose={closeTour}
        onPrev={prevTourStep}
        onNext={nextTourStep}
      />

      {/* Custom scrollbar styles + featured widget animation */}
      <style>{`
        .custom-scrollbar::-webkit-scrollbar { width: 5px; }
        .custom-scrollbar::-webkit-scrollbar-track { background: transparent; }
        .custom-scrollbar::-webkit-scrollbar-thumb { background: rgba(255,255,255,0.14); border-radius: 3px; }
        .custom-scrollbar::-webkit-scrollbar-thumb:hover { background: rgba(255,255,255,0.25); }
        @keyframes zoom-in-95 { from { opacity: 0; transform: scale(0.95); } to { opacity: 1; transform: scale(1); } }
        .animate-in { animation: zoom-in-95 0.2s ease-out; }
      `}</style>
    </div>
    </div>
    </FeaturedContext.Provider>
  );
};

export default SpursOKCDashboard;
